/**
 * 콘텐츠 — 자산과 플레이리스트.
 *
 * 두 화면을 한 파일에 두는 이유: 실제 작업이 "자산을 올리고 바로 편성에 넣는" 흐름으로
 * 이어지기 때문이다. 코드도 같이 두면 그 흐름을 유지하기 쉽다.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  FileText,
  Grid2x2,
  Image as ImageIcon,
  List,
  PanelsTopLeft,
  Pencil,
  Play,
  Scaling,
} from 'lucide-react';
import { TEMPLATE_META, type TemplateId } from '@signageone/core';
import { LIST_CAP, type Asset, type Me, type Playlist, type PlaylistItem, type SignageOneClient } from '../api/client.js';
import {
  Badge,
  Empty,
  ErrorNote,
  formatBytes,
  formatSeconds,
  ItemTitle,
  Loading,
  Modal,
  Panel,
  RelTime,
  rowAction,
} from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { DateTimeField } from '../components/DateField.js';
import { siteOf, TemplateThumb, VideoThumb, WebThumb } from '../components/MediaThumbs.js';
import { HtmlThumb } from '../components/HtmlContent.js';
import { AssetThumbSmall } from '../components/AssetThumbSmall.js';
import { externalSummary } from '../components/HtmlExternalNote.js';
import { useBrand } from '../components/brand.js';
import { HtmlFitModal } from '../components/HtmlFitModal.js';
import { useHtmlAutoMeasure } from '../components/htmlMeasure.js';
import { TemplateEditor, TemplatePicker } from './TemplateEditor.js';
import { NewScreenEditor, TemplateCopyEditor } from '../htmlEditor/ScreenEditor.js';
import { AssetEditModal, ValidityFields, parseTagList } from './AssetEditor.js';
import { can } from '../capabilities.js';
import { useAction, useAsync, useDebounced } from '../hooks.js';
import { reorderItems, toggleContent } from '../content-rules.js';
import { readValidity, validityBadge } from '../validity.js';
import { isPdfFile, pdfBaseName, pdfFailureText, pdfToPages } from '../pdfPages.js';
import { HistoryButton } from '../components/HistoryModal.js';
import { assetStatusBadge, useTranscodePolling } from '../transcode.js';
import { TRANSITION_DESCRIPTIONS, TRANSITION_OPTIONS } from '../transitions.js';
import { TransitionGuideButton } from '../components/TransitionPreview.js';
import { notify } from '../components/Toast.js';
import { BlockedDeleteModal } from '../components/BlockedDeleteModal.js';
import { blockedBy, type BlockedBy } from '../references.js';
import { ColumnHead, FilterMenu, FilterNote, useTableSort } from '../components/TableHead.js';
import { FilterField, FilterPopover } from '../components/FilterPopover.js';
import { MoreMenu } from '../components/MoreMenu.js';
import { Disclosure } from '../components/Disclosure.js';
import { useListReturn, useUrlQuery, useUrlText } from '../components/urlState.js';
import {
  applyFilters,
  filterOptions,
  fixedOrder,
  sortRows,
  type Filters,
  type SortDir,
  type SortValue,
} from '../components/tableView.js';
import { useDirty, useGuardedClose } from '../components/closeGuard.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { copyName } from '../components/copyName.js';
import { IconButton } from '../components/IconButton.js';
import { focusFirstInvalid, NumberInput, numberProblem, type NumberRule } from '../components/form/NumberInput.js';
import {
  PageSelectionNote,
  runEach,
  SelectAll,
  SelectionBar,
  useKeepSelectionWithin,
} from '../components/SelectionBar.js';
import { Pager, usePaging } from '../components/Pager.js';
import { paginate } from '../components/paging.js';

interface Props {
  client: SignageOneClient;
  /** 권한에 따라 만들기 단추를 낼지 정한다. 서버가 최종 관문이고, 여기서는 감출 뿐이다. */
  me: Me;
}

/** 목록에 영문 유형이 그대로 나오면 읽는 사람이 매번 번역해야 한다. */
const TYPE_LABEL: Record<Asset['type'], string> = {
  image: '이미지',
  video: '영상',
  web: '웹',
  template: '템플릿',
  html: 'HTML',
};

/**
 * 형식 필터의 차례.
 *
 * `TYPE_LABEL` 을 그대로 쓰지 않는 이유는 순서다. 객체 키 순서에 화면 순서를
 * 맡기면 라벨을 하나 손볼 때 필터의 차례가 같이 흔들린다.
 */
const TYPE_ORDER = ['image', 'video', 'template', 'web', 'html'] as const;

type TypeFilter = 'all' | Asset['type'];

/**
 * 지금 목록을 좁히고 있는 조건을 한 문장으로.
 *
 * 아무것도 걸리지 않았으면 `null` 이다. 빈 목록이 **"등록된 것이 없다"** 인지
 * **"걸러서 안 보인다"** 인지는 이 값 하나로 갈린다. 둘을 같은 문장으로 말하면
 * 사용자는 이미 있는 것을 다시 올린다.
 *
 * 보관함 토글은 여기 넣지 않는다. 그것은 목록을 **넓히는** 쪽이라, 비어 있는
 * 이유가 될 수 없다.
 */
/** 게시 기간 거르기 — 서버의 `validity` 값과 같다. 빈 문자열이면 거르지 않는다. */
export type ValidityFilter = '' | 'expiring' | 'expired' | 'scheduled' | 'limited';

export const ASSET_SORT_KEYS = ['title', 'size', 'usage', 'created'] as const;
export type AssetSortKey = (typeof ASSET_SORT_KEYS)[number];

export function assetSortValue(asset: Asset, key: AssetSortKey): SortValue {
  if (key === 'title') return asset.title || null;
  // 웹 콘텐츠는 크기가 없다("—") — 0 처럼 앞에 세우지 않는다.
  if (key === 'size') return asset.type === 'web' ? null : asset.size;
  // 쓰는 편성 수 — 적은 순이면 아무 데도 안 쓰는 콘텐츠가 먼저 온다(정리할 것).
  if (key === 'usage') return asset.used_in?.length ?? 0;
  return Date.parse(asset.created_at);
}

/**
 * 그리드 보기의 정렬 고르기 — 표 머리가 없어 한 칸에 모은다. 표 보기와 같은 정렬을 쓴다(바꾸면 둘 다).
 * 값은 "열쇠:방향" 이다.
 */
export const ASSET_SORT_CHOICES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'created:desc', label: '최근 등록 순' },
  { value: 'created:asc', label: '오래된 등록 순' },
  { value: 'title:asc', label: '제목 가나다순' },
  { value: 'size:desc', label: '크기 큰 순' },
  { value: 'usage:asc', label: '편성 적은 순' },
];

/** 주소의 `?type` 읽기 — 모르는 값은 전체. */
export function parseTypeParam(value: string): TypeFilter {
  return value === 'image' || value === 'video' || value === 'web' || value === 'template' || value === 'html' ? value : 'all';
}

export const VALIDITY_FILTER_LABEL: Readonly<Record<Exclude<ValidityFilter, ''>, string>> = {
  expiring: '7일 안에 게시 종료',
  expired: '게시 종료됨',
  scheduled: '게시 시작 전',
  limited: '게시 기간 있음',
};

/** 주소의 `?validity` 읽기 — 모르는 값은 거르지 않는다. */
export function parseValidityParam(value: string): ValidityFilter {
  // `in` 은 `toString` 같은 물려받은 이름에도 참이다 — 제 것만 본다.
  return Object.hasOwn(VALIDITY_FILTER_LABEL, value) ? (value as ValidityFilter) : '';
}

export function describeFilters(input: {
  type: TypeFilter;
  search: string;
  tag?: string;
  validity?: ValidityFilter;
}): string | null {
  const parts = [
    input.type === 'all' ? null : `${TYPE_LABEL[input.type]} 형식`,
    input.tag?.trim() ? `태그 "${input.tag.trim()}"` : null,
    input.validity ? VALIDITY_FILTER_LABEL[input.validity] : null,
    input.search.trim() ? `"${input.search.trim()}" 검색` : null,
  ].filter((part): part is string => part !== null);

  return parts.length > 0 ? parts.join(' · ') : null;
}

/** 보기 방식을 기억한다 — 매번 고르게 하면 그것부터가 번거롭다. */
const VIEW_KEY = 'signageone.content.view';

function readView(): 'grid' | 'list' {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

/** [업로드 옵션] 이 접혀 있을 때 머리에 적는 지금 값 — 비었으면 "태그·게시 종료" 로 무엇이 있는지만 알린다. */
export function uploadOptionsSummary(tags: string, until: string): string {
  const parts: string[] = [];
  if (tags.trim()) parts.push(`태그 ${tags.trim()}`);
  if (until) parts.push(`${until.replace('T', ' ')} 까지`);
  return parts.length > 0 ? parts.join(' · ') : '태그·게시 종료';
}

export function AssetsScreen({ client, me }: Props): ReactNode {
  /** 콘텐츠를 올리고 고칠 수 있는가. 없으면 쓰기 단추를 그리지 않는다. */
  const canWrite = can(me, 'content:write');
  const [view, setView] = useState<'grid' | 'list'>(readView);
  const [creating, setCreating] = useState(false);
  /*
   * 필터는 주소에 산다(`?q=·type=·tag=·validity=·archived=`) — 상세를 열었다 돌아와도, 새로 고쳐도
   * 그대로다. 대시보드의 '곧 끝나는 콘텐츠' 카드도 `?validity=expiring` 으로 이 화면을 연다.
   */
  const url = useUrlQuery();
  const showArchived = url.get('archived') === '1';
  const setShowArchived = (next: boolean): void => url.set({ archived: next ? '1' : null });
  const [searchText, setSearch] = useUrlText(url, 'q');
  /*
   * 서버에 묻는 검색어는 **입력이 멎은 뒤의 것**이다. 주소는 글자마다 바뀌는데(`useUrlText`), 그대로
   * 조회에 물리면 한 글자 — 한글은 조합 단계마다 — 콘텐츠 전부를 쪽 끝까지 다시 받는다. 입력칸은
   * 화면 상태(`searchText`)로 그리므로 늦지 않고, 한글 조합도 끊기지 않는다.
   */
  const search = useDebounced(url.get('q'), 250);
  /*
   * 형식 필터.
   *
   * 거르는 일은 서버가 한다. 받아 온 페이지에서 걸러 내면 "영상만" 을 골랐을 때
   * 첫 페이지에 영상이 없다는 이유로 빈 화면이 나오는데, 그것은 영상이 없다는
   * 뜻이 아니라 **아직 안 받았다**는 뜻이라 사용자가 알 방법이 없다.
   */
  const typeFilter = parseTypeParam(url.get('type'));
  const setTypeFilter = (next: TypeFilter): void => url.set({ type: next === 'all' ? null : next });
  /*
   * 태그 필터. 빈 문자열이면 거르지 않는다.
   *
   * **고를 수 있는 값은 서버가 센다**(`/assets/tags`). 받아 온 페이지에서 추리면 콘텐츠가
   * 한 페이지를 넘는 순간 드롭다운에서 태그가 빠진다 — 거르기가 필요한 규모에서 틀리는 셈이다.
   */
  const tagFilter = url.get('tag');
  const setTagFilter = (next: string): void => url.set({ tag: next || null });
  /*
   * 게시 기간 거르기 — "곧 끝나는 것" 을 한 번에 본다. 대시보드의 '곧 끝나는 콘텐츠' 카드가
   * 이 값으로 이 화면을 연다(`?validity=expiring`).
   */
  const validityFilter = parseValidityParam(url.get('validity'));
  const setValidityFilter = (next: ValidityFilter): void => url.set({ validity: next || null });
  /** 고른 콘텐츠의 게시 기간을 한꺼번에 정하는 창. */
  const [validityOpen, setValidityOpen] = useState(false);
  const [webOpen, setWebOpen] = useState(false);
  /** [빈 화면에서 만들기] — 이름·방향을 고른 뒤 빈 화면으로 화면 편집기를 연다. */
  const [blankOpen, setBlankOpen] = useState(false);
  /*
   * 올릴 때 붙일 태그.
   *
   * **붙일 자리가 올리는 자리에 있어야 한다.** 예전에는 태그를 다는 길이 [콘텐츠 정보 수정]
   * 뿐이어서, 스무 장을 올린 뒤 한 장씩 스무 번 열어야 했다 — 그래서 아무도 달지 않았고,
   * 태그로 고르는 캠페인·검색이 빈 채로 남았다. 여기 적은 태그가 이번 묶음 전체에 붙는다.
   */
  const [uploadTags, setUploadTags] = useState('');
  /** 올릴 때 붙일 게시 종료(`datetime-local`). 계약 기간이 정해진 소재는 올리는 자리에서 적는다. */
  const [uploadUntil, setUploadUntil] = useState('');
  const [uploadTagError, setUploadTagError] = useState<string | null>(null);
  // 템플릿 편집기 상태 — 종류 고르기 → 새로 만들기 / 기존 편집.
  const [picking, setPicking] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState<TemplateId | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<Asset | null>(null);
  /** 등록한 템플릿에서 새로 만들기(2026-10-30) — 템플릿은 템플릿 편집기로, 올린 HTML 은 사본의 화면 편집으로. */
  const [fromTemplate, setFromTemplate] = useState<Asset | null>(null);
  /** 제목·노출 시간·태그(웹은 주소) 수정. 템플릿 내용은 위 편집기가 맡는다. */
  const [editingInfo, setEditingInfo] = useState<Asset | null>(null);
  /** 그리드에서 고른 콘텐츠. 하나라도 고르면 모든 카드의 체크박스가 상시 보인다. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** 마지막 일괄 삭제 결과. 건너뛴 것이 있으면 사유를 보여준다. */
  const [deleteReport, setDeleteReport] = useState<{
    deleted: number;
    skipped: Array<{ id: string; title: string; reason: string; playlists: string[]; schedules?: string[] }>;
  } | null>(null);

  const assets = useAsync(
    () =>
      client.allAssets({
        archived: showArchived ? 'all' : 'false',
        q: search,
        type: typeFilter === 'all' ? undefined : typeFilter,
        tag: tagFilter || undefined,
        validity: validityFilter || undefined,
      }),
    [client, showArchived, search, typeFilter, tagFilter, validityFilter],
  );
  const tags = useAsync(() => client.listAssetTags(), [client]);
  const action = useAction();
  // 변환 중인 영상이 있으면 진행률이 움직이게 목록을 다시 묻는다 — 끝나거나, 진행이 멈추거나, 탭이 뒤로 가면 멈춘다.
  useTranscodePolling(assets.data?.items ?? [], assets.reload, 5000);
  // 아직 크기를 재지 않은 올린 HTML 을 잰다 — 올리면 목록이 새로 그려지며 곧바로 잰다(`htmlMeasure.ts`).
  useHtmlAutoMeasure(client, assets.data?.items, canWrite, assets.reload);
  /**
   * 콘텐츠가 바뀌면 목록과 태그 드롭다운을 **함께** 새로 고친다.
   *
   * 등록·수정이 태그를 만들고 없앤다. 목록만 새로 고치면 방금 붙인 태그가 드롭다운에
   * 없어서 그것으로 거를 수 없고, 마지막 하나를 뗀 태그는 계속 남아 빈 목록을 부른다.
   */
  const reloadAll = (): void => {
    assets.reload();
    tags.reload();
  };
  /**
   * [화면 맞춤] 창에 띄울 올린 HTML — 올린 직후 저절로(올린 것 가운데 HTML 만), 목록의 [화면 맞춤] 으로.
   * 상세로 들어가야 맞출 수 있으면 올린 사람은 그대로 편성에 넣고, 화면에서 반만 보이는 것을 그때 안다.
   */
  const [fitting, setFitting] = useState<string[] | null>(null);
  const upload = useUploader(
    client,
    reloadAll,
    (ids) => {
      if (canWrite && ids.length > 0) setFitting(ids);
    },
    // 여러 쪽 PDF 를 쪽 순서대로 담은 플레이리스트 — 만드는 것은 편성 변경이다(`schedule:write`).
    can(me, 'schedule:write'),
  );
  const loadedItems = assets.data?.items ?? [];
  /** 상세로 가는 길 — 돌아오면 이 목록의 보던 자리로(`useListReturn`). */
  const openDetail = useListReturn(assets.data !== null);
  /* 정렬 — 기본은 서버가 주는 순서(최근 등록)와 같다. 그리드·표가 같은 순서를 쓴다. */
  const [sort, toggleSort, chooseSort] = useTableSort<AssetSortKey>('assets', ASSET_SORT_KEYS, {
    key: 'created',
    dir: 'desc',
  });
  const items = useMemo(() => sortRows(loadedItems, sort, assetSortValue), [loadedItems, sort]);
  /*
   * 쪽 나누기(2026-10-21) — 정렬한 전부를 쪽으로 자른다. 수천 장의 썸네일을 한 번에 그리지 않고, 끝없는
   * 스크롤에서 보던 자리를 잃지 않는다. 검색·거르기·정렬을 바꾸면 1쪽으로.
   */
  const paging = usePaging(
    url,
    'assets',
    JSON.stringify([showArchived, search, typeFilter, tagFilter, validityFilter, sort.key, sort.dir]),
  );
  const pageSlice = paginate(items, paging.page, paging.size);
  const pageRows = pageSlice.rows;
  /*
   * 고른 것은 쪽을 넘겨도 남고(막대가 "다른 쪽 N건 포함" 을 적는다), 검색·거르기로 목록에서 빠진 것만 선택에서 뺀다.
   */
  const loadedIds = useMemo(() => loadedItems.map((asset) => asset.id), [loadedItems]);
  useKeepSelectionWithin(loadedIds, setSelected);
  const sorting = (dirLabels: Record<SortDir, string>, firstDir: SortDir = 'asc') => ({
    sort,
    onSort: toggleSort,
    dirLabels,
    firstDir,
  });

  /** 지금 걸린 조건. null 이면 거르는 것이 없다 — 빈 목록의 이유가 갈린다. */
  const filterSummary = describeFilters({ type: typeFilter, search, tag: tagFilter, validity: validityFilter });

  // 주소를 한 번에 고친다 — 셋을 따로 고치면 뒤의 것이 앞의 것을 덮는다(`useUrlQuery`).
  const clearFilters = (): void => url.set({ type: null, q: null, tag: null, validity: null });

  /** 템플릿은 내용까지 고치는 편집기로, 나머지는 정보 수정으로 간다. */
  const edit = (asset: Asset): void => {
    if (asset.type === 'template' && asset.template) setEditingTemplate(asset);
    else setEditingInfo(asset);
  };

  const archive = (asset: Asset): void => {
    void action.run(
      () => client.updateAsset(asset.id, { archived: !asset.archived }),
      assets.reload,
      asset.archived ? `"${asset.title}" 을(를) 복원했습니다` : `"${asset.title}" 을(를) 보관했습니다`,
    );
  };

  const toggleSelection = (id: string): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * 선택한 콘텐츠를 삭제한다. **되돌릴 수 없다.**
   *
   * 편성에 물린 자산은 서버가 거부하므로 **일부만 지워지는 것이 정상이다.**
   * 결과를 뭉뚱그려 "몇 개 지웠다" 로만 말하면, 남아 있는 것을 사용자가 눈으로
   * 세게 된다. 무엇이 왜 남았는지 그대로 보여준다.
   */
  const deleteSelected = async (): Promise<void> => {
    const chosen = items.filter((asset) => selected.has(asset.id));
    const preview = chosen.slice(0, 5).map((asset) => asset.title).join(', ');
    const more = chosen.length > 5 ? ` 외 ${chosen.length - 5}건` : '';
    const ok = await confirmDialog({
      title: `콘텐츠 ${chosen.length}건을 삭제할까요?`,
      body: `${preview}${more}\n\n되돌릴 수 없으며, 편성에 사용 중인 콘텐츠는 건너뜁니다.`,
      confirmLabel: '삭제',
      danger: true,
    });
    if (!ok) return;

    void action.run(
      async () => {
        const result = await client.bulkDeleteAssets([...selected]);
        setDeleteReport(result);
        return result;
      },
      () => {
        setSelected(new Set());
        assets.reload();
      },
    );
  };

  return (
    <div className="screen">
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      {assets.error ? <ErrorNote message={assets.error} /> : null}

      <Panel
        title={`콘텐츠 ${items.length}건`}
        className="screen-fill"
        actions={
          <>
            <input
              className="input input-search"
              type="search"
              placeholder="제목 검색"
              value={searchText}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select
              className="input"
              value={typeFilter}
              onChange={(event) => {
                setTypeFilter(event.target.value as TypeFilter);
                // 안 보이는 것이 선택된 채로 남으면 일괄 삭제가 엉뚱한 것을 지운다.
                setSelected(new Set());
              }}
              aria-label="형식 필터"
            >
              <option value="all">전체 형식</option>
              {TYPE_ORDER.map((type) => (
                <option key={type} value={type}>
                  {TYPE_LABEL[type]}
                </option>
              ))}
            </select>
            {/*
              태그·게시 기간·보관함은 [필터] 뒤로 접는다(점진적 공개) — 머리에는 늘 쓰는 검색·형식만.
              접혀도 걸린 개수는 단추에 남는다.

              태그 고르개는 **붙어 있는 태그가 있을 때만** 낸다. 고를 것이 없는 빈 고르개는 자리만
              차지하고, 태그를 안 쓰는 곳에서는 늘 비어 있다. 지금 걸린 태그가 목록에서 사라져도(마지막
              콘텐츠의 태그를 뗐다) 그 값을 남겨 둔다 — 조용히 전체로 돌아가면 "왜 갑자기 다 보이나" 가 된다.
            */}
            <FilterPopover
              activeCount={(tagFilter ? 1 : 0) + (validityFilter ? 1 : 0) + (showArchived ? 1 : 0)}
              onReset={() => {
                // 한 번에 고친다 — 주소를 셋으로 나눠 고치면 앞의 것이 뒤의 것에 덮인다.
                url.set({ tag: null, validity: null, archived: null });
                setSelected(new Set());
              }}
            >
              {(tags.data?.items.length ?? 0) > 0 || tagFilter ? (
                <FilterField label="태그">
                  <select
                    className="input"
                    value={tagFilter}
                    onChange={(event) => {
                      setTagFilter(event.target.value);
                      // 안 보이는 것이 선택된 채로 남으면 일괄 삭제가 엉뚱한 것을 지운다.
                      setSelected(new Set());
                    }}
                    aria-label="태그 필터"
                  >
                    <option value="">전체 태그</option>
                    {tagFilter && !(tags.data?.items ?? []).some((tag) => tag.name === tagFilter) ? (
                      <option value={tagFilter}>{tagFilter}</option>
                    ) : null}
                    {(tags.data?.items ?? []).map((tag) => (
                      <option key={tag.name} value={tag.name}>
                        {tag.name} ({tag.asset_count})
                      </option>
                    ))}
                  </select>
                </FilterField>
              ) : null}
              <FilterField label="게시 기간">
                <select
                  className="input"
                  value={validityFilter}
                  onChange={(event) => {
                    setValidityFilter(event.target.value as ValidityFilter);
                    setSelected(new Set());
                  }}
                  aria-label="게시 기간 필터"
                >
                  <option value="">전체 게시 기간</option>
                  {(Object.keys(VALIDITY_FILTER_LABEL) as Array<Exclude<ValidityFilter, ''>>).map((value) => (
                    <option key={value} value={value}>
                      {VALIDITY_FILTER_LABEL[value]}
                    </option>
                  ))}
                </select>
              </FilterField>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(event) => setShowArchived(event.target.checked)}
                />
                보관함 보기
              </label>
            </FilterPopover>

            {/* 그리드에는 표 머리가 없어 정렬을 여기서 고른다. 목록 보기에서는 표 머리를 누른다. */}
            {view === 'grid' ? (
              <select
                className="input"
                aria-label="정렬"
                value={`${sort.key}:${sort.dir}`}
                onChange={(event) => {
                  const [key, dir] = event.target.value.split(':') as [AssetSortKey, SortDir];
                  chooseSort({ key, dir });
                }}
              >
                {/* 표 머리에서 고른 정렬이 목록에 없으면 그 값도 보여 준다 — 빈 칸으로 두면 무엇으로 정렬했는지 모른다. */}
                {ASSET_SORT_CHOICES.some((choice) => choice.value === `${sort.key}:${sort.dir}`) ? null : (
                  <option value={`${sort.key}:${sort.dir}`}>표에서 고른 정렬</option>
                )}
                {ASSET_SORT_CHOICES.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
            ) : null}
            {/* 그리드 ↔ 목록. 사이니지 콘텐츠는 대개 눈으로 고르므로 그리드가 기본이다. */}
            <div className="seg">
              {(
                [
                  ['grid', Grid2x2, '그리드로 보기'],
                  ['list', List, '목록으로 보기'],
                ] as const
              ).map(([mode, Icon, label]) => (
                <button
                  key={mode}
                  type="button"
                  aria-label={label}
                  aria-pressed={view === mode}
                  onClick={() => {
                    setView(mode);
                    try {
                      window.localStorage.setItem(VIEW_KEY, mode);
                    } catch {
                      // 저장이 막혀도 이번 세션에는 적용된다.
                    }
                  }}
                  className="seg-btn seg-btn-icon"
                >
                  <Icon className="h-4 w-4" strokeWidth={2} />
                </button>
              ))}
            </div>

            {/*
              만들기 경로가 셋인데 버튼 셋을 나란히 두면 무엇이 기본인지 알 수 없다.
              가장 흔한 것(파일 올리기)을 주 버튼으로 두고 나머지를 접는다.

              좁은 화면에서 줄이 바뀌면 이 버튼을 줄 오른쪽 끝으로 민다. 메뉴가 버튼
              오른쪽에 맞춰 왼쪽으로 펼쳐지므로, 버튼이 줄 왼쪽에 서면 메뉴가 패널 밖으로
              나가 잘린다.
            */}
            {/* 올릴 수 없는 사람에게는 [콘텐츠 추가] 를 내지 않는다 — 셋 다 `content:write` 다. */}
            {canWrite ? (
            <div className="relative max-lg:ml-auto">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setCreating((prev) => !prev)}
              >
                콘텐츠 추가
                <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
              {creating ? (
                <>
                  <div className="fixed inset-0 z-40" role="presentation" onClick={() => setCreating(false)} />
                  <div className="absolute right-0 top-10 z-50 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                    {/*
                      태그는 파일을 고르기 **전에** 적는다. 고른 순간 업로드가 시작되므로
                      뒤에 물으면 이미 올라가 있다.
                    */}
                    {/*
                      태그·게시 종료는 대개 비워 두는 칸이라 [업로드 옵션] 뒤에 접는다(점진적 공개) — 메뉴를 열면
                      가장 흔한 [파일 올리기] 가 먼저 보인다. 펼친 채로 열고, 쓰지 않으면 접는다.
                    */}
                    <div className="border-b border-slate-100 px-4 pb-3 pt-3">
                      <Disclosure
                        className="is-plain"
                        title="업로드 옵션"
                        summary={uploadOptionsSummary(uploadTags, uploadUntil)}
                      >
                        <label className="block text-[12px] font-medium text-slate-600" htmlFor="upload-tags">
                          태그 <span className="font-normal text-slate-400">(선택)</span>
                          <HelpTip
                            text="쉼표로 구분합니다. 이번에 업로드하는 파일 전체에 적용됩니다."
                            label="업로드 태그 도움말"
                          />
                        </label>
                        <input
                          id="upload-tags"
                          className="input mt-1.5"
                          value={uploadTags}
                          placeholder="로비, 가을 프로모션"
                          onChange={(event) => {
                            setUploadTags(event.target.value);
                            setUploadTagError(null);
                          }}
                        />
                        <label className="mt-3 block text-[12px] font-medium text-slate-600" htmlFor="upload-until">
                          게시 종료 <span className="font-normal text-slate-400">(선택)</span>
                          <HelpTip
                            text="이 시각부터 어느 편성에 있어도 화면에 나가지 않습니다. 광고 모델 계약·행사처럼 끝이 정해진 소재에 적으세요."
                            label="게시 종료 도움말"
                          />
                        </label>
                        <div id="upload-until" className="mt-1.5">
                          <DateTimeField
                            label="게시 종료"
                            defaultTime="23:59"
                            value={uploadUntil}
                            onChange={(next) => {
                              setUploadUntil(next);
                              setUploadTagError(null);
                            }}
                          />
                        </div>
                        {uploadTagError ? (
                          <p className="mt-1 text-[11.5px] text-red-600" role="alert">
                            {uploadTagError}
                          </p>
                        ) : null}
                      </Disclosure>
                    </div>
                    <UploadButton
                      busy={upload.progress !== null}
                      onFiles={(files) => {
                        const parsed = parseTagList(uploadTags);
                        if ('error' in parsed) {
                          // 메뉴를 닫지 않는다 — 고칠 칸이 이 안에 있다.
                          setUploadTagError(parsed.error);
                          return;
                        }
                        const validity = readValidity('', uploadUntil);
                        if ('error' in validity) {
                          setUploadTagError(validity.error);
                          return;
                        }
                        // 메뉴는 곧바로 닫는다. 진행과 결과는 메뉴 밖(목록 위)에 남는다 —
                        // 예전에는 진행 표시가 메뉴 안에 있어, 첫 파일이 올라가며 메뉴가 닫히는 순간 함께 사라졌다.
                        setCreating(false);
                        void upload.start(files, parsed.tags, validity.valid_until);
                      }}
                    />
                    <button
                      type="button"
                      className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-left text-[13.5px] text-slate-700 hover:bg-slate-50"
                      onClick={() => {
                        setCreating(false);
                        setPicking(true);
                      }}
                    >
                      <FileText className="h-4 w-4 text-slate-400" strokeWidth={1.9} />
                      템플릿 만들기
                    </button>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-left text-[13.5px] text-slate-700 hover:bg-slate-50"
                      onClick={() => {
                        setCreating(false);
                        setBlankOpen(true);
                      }}
                    >
                      <PanelsTopLeft className="h-4 w-4 text-slate-400" strokeWidth={1.9} />
                      빈 화면에서 만들기
                    </button>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-left text-[13.5px] text-slate-700 hover:bg-slate-50"
                      onClick={() => {
                        setCreating(false);
                        setWebOpen(true);
                      }}
                    >
                      <Play className="h-4 w-4 text-slate-400" strokeWidth={1.9} />
                      웹 URL 추가
                    </button>
                  </div>
                </>
              ) : null}
            </div>
            ) : null}
          </>
        }
      >
        {/*
          선택 막대. 하나라도 고르면 나타난다.

          삭제는 되돌릴 수 없고 보관은 되돌릴 수 있다 — 둘을 나란히 두되 보관을
          먼저, 평범하게 둔다. 목록을 정리하려던 사람이 삭제를 누르지 않도록.
        */}
        {selected.size > 0 && canWrite ? (
          <div className="bulkbar">
            <span className="bulkbar-count">{selected.size}건 선택됨</span>
            <PageSelectionNote
              pageIds={pageRows.map((asset) => asset.id)}
              allIds={loadedIds}
              selected={selected}
              unit="건"
              onChange={setSelected}
            />
            <button type="button" className="btn" disabled={action.pending} onClick={() => setValidityOpen(true)}>
              게시 기간
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={action.pending}
              onClick={deleteSelected}
            >
              {action.pending ? '삭제 중…' : '삭제'}
            </button>
            <button type="button" className="link" onClick={() => setSelected(new Set())}>
              선택 해제
            </button>
          </div>
        ) : null}

        {/* 업로드 진행과 결과 — 메뉴가 닫혀도 남도록 목록 위에 둔다. */}
        {upload.progress ? (
          <div className="mx-4 mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-[12.5px] text-blue-900" role="status">
            업로드 중… {upload.progress.done}/{upload.progress.total} · 완료될 때까지 화면을 닫지 마세요.
            {upload.progress.detail ? <div className="mt-0.5 text-blue-800/80">{upload.progress.detail}</div> : null}
          </div>
        ) : null}
        {!upload.progress && upload.message ? (
          <div
            className={`mx-4 mt-4 rounded-lg border p-3 text-[12.5px] ${
              upload.message.warn ? 'border-amber-200 bg-amber-50' : 'border-blue-200 bg-blue-50'
            }`}
            role="status"
          >
            <p className={`whitespace-pre-line ${upload.message.warn ? 'text-amber-900' : 'text-blue-900'}`}>{upload.message.text}</p>
            <button type="button" className="link mt-1.5" onClick={upload.clearMessage}>
              닫기
            </button>
          </div>
        ) : null}

        {/*
          일괄 삭제 결과. 건너뛴 것이 있을 때만 남긴다 — 전부 지워졌으면
          목록에서 사라진 것으로 충분하고, 굳이 한 번 더 말할 이유가 없다.
        */}
        {deleteReport && deleteReport.skipped.length > 0 ? (
          <div className="mx-4 mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12.5px]">
            <p className="font-medium text-amber-900">
              {deleteReport.deleted}건 삭제 · {deleteReport.skipped.length}건 건너뜀(편성에 사용 중)
            </p>
            <ul className="mt-1.5 space-y-0.5 text-amber-800">
              {deleteReport.skipped.map((item) => (
                <li key={item.id}>
                  {item.title} — {[...item.playlists, ...(item.schedules ?? []).map((name) => `스케줄 ‘${name}’`)].join(', ')}
                </li>
              ))}
            </ul>
            <button type="button" className="link mt-1.5" onClick={() => setDeleteReport(null)}>
              닫기
            </button>
          </div>
        ) : null}

        {assets.loading && items.length === 0 ? <Loading /> : null}

        {assets.data?.next_cursor ? (
          <div className="filter-note" role="status">
            <span>
              콘텐츠가 {LIST_CAP.toLocaleString('ko-KR')}개를 넘어 최근 등록한 {items.length.toLocaleString('ko-KR')}개만
              보입니다 — 검색이나 형식·태그로 좁혀 보세요.
            </span>
          </div>
        ) : null}

        {/*
          빈 목록의 이유를 구분한다.

          거르고 있는데 "등록된 콘텐츠가 없습니다" 라고 하면, 있는 것을 없다고 말하는
          셈이다. 올리라는 안내까지 따라붙으면 같은 것을 두 번 올리게 된다.
        */}
        {!assets.loading && items.length === 0 ? (
          filterSummary ? (
            <Empty
              title="조건에 맞는 콘텐츠가 없습니다"
              hint={`적용된 조건: ${filterSummary}`}
              action={
                <button type="button" className="btn mt-3" onClick={clearFilters}>
                  조건 지우기
                </button>
              }
            />
          ) : (
            <Empty
              title="등록된 콘텐츠가 없습니다"
              hint="이미지·영상을 업로드하거나 웹 URL을 추가하세요."
            />
          )
        ) : null}

        {items.length > 0 && view === 'grid' ? (
          <AssetGrid
            assets={pageRows}
            selected={selected}
            onToggleSelect={toggleSelection}
            onOpen={(asset) => openDetail(`/content/${asset.id}`)}
            onEdit={edit}
            onArchive={archive}
            onFit={(asset) => setFitting([asset.id])}
            canWrite={canWrite}
          />
        ) : null}

        {items.length > 0 && view === 'list' ? (
          <div className="table-scroll">
          <table className="table min-w-[40rem]">
            <thead>
              <tr>
                <ColumnHead label="제목" column="title" sorting={sorting({ asc: '가나다순', desc: '가나다 역순' })} />
                <th>형식</th>
                <ColumnHead label="크기" column="size" sorting={sorting({ asc: '작은 순', desc: '큰 순' }, 'desc')} />
                {/* 적은 순이 먼저다 — 이 열을 누르는 사람은 안 쓰는 콘텐츠를 정리하는 중이다. */}
                <ColumnHead label="편성" column="usage" sorting={sorting({ asc: '적은 순', desc: '많은 순' })} />
                <ColumnHead label="등록" column="created" sorting={sorting({ asc: '오래된 순', desc: '최근 순' }, 'desc')} />
                <th />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((asset) => (
                <tr
                  key={asset.id}
                  className={asset.archived ? 'row-muted' : undefined}
                  {...rowAction(() => openDetail(`/content/${asset.id}`))}
                >
                  <td className="cell-strong">
                    {asset.title}
                    {asset.archived ? <Badge tone="neutral">보관됨</Badge> : null}
                    <AssetStatusBadge asset={asset} />
                    <ValidityBadgeView asset={asset} />
                  </td>
                  <td>
                    <Badge tone="info">{TYPE_LABEL[asset.type]}</Badge>
                  </td>
                  <td className="cell-muted">{asset.type === 'web' ? '—' : formatBytes(asset.size)}</td>
                  {/* 어느 편성에 들어 있는지 — 삭제가 막히는 이유가 여기 그대로 보인다. */}
                  <td className="cell-muted" title={usageTitle(asset)}>
                    {usageLabel(asset)}
                  </td>
                  <td className="cell-muted"><RelTime iso={asset.created_at} /></td>
                  {/* 행 클릭은 상세로 간다 — 행 안의 버튼은 그 이동을 막는다. */}
                  <td className="col-actions" onClick={(event) => event.stopPropagation()}>
                    {/* 고칠 수 없는 사람에게는 행 동작을 내지 않는다 — 둘 다 `content:write` 다. */}
                    {canWrite ? (
                      <>
                        <IconButton icon={Pencil} label="편집" subject={asset.title} onClick={() => edit(asset)} />
                        {asset.type === 'html' ? (
                          <IconButton icon={Scaling} label="화면 맞춤" subject={asset.title} onClick={() => setFitting([asset.id])} />
                        ) : null}
                        <IconButton
                          icon={asset.archived ? ArchiveRestore : Archive}
                          label={asset.archived ? '복원' : '보관'}
                          subject={asset.title}
                          onClick={() => archive(asset)}
                        />
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : null}

        <Pager slice={pageSlice} paging={paging} />
      </Panel>

      {webOpen ? (
        <WebAssetModal
          client={client}
          initialTags={uploadTags}
          onClose={() => setWebOpen(false)}
          onCreated={() => {
            setWebOpen(false);
            reloadAll();
          }}
        />
      ) : null}

      {blankOpen ? (
        <NewScreenEditor
          client={client}
          tags={(() => {
            const parsed = parseTagList(uploadTags);
            return 'error' in parsed ? [] : parsed.tags;
          })()}
          onClose={() => {
            setBlankOpen(false);
            reloadAll();
          }}
        />
      ) : null}

      {picking ? (
        <TemplatePicker
          client={client}
          onClose={() => setPicking(false)}
          onPick={(id) => {
            setPicking(false);
            setCreatingTemplate(id);
          }}
          onPickRegistered={(asset) => {
            setPicking(false);
            setFromTemplate(asset);
          }}
        />
      ) : null}

      {fromTemplate?.type === 'template' ? (
        <TemplateEditor
          client={client}
          from={fromTemplate}
          onClose={() => setFromTemplate(null)}
          onSaved={() => {
            setFromTemplate(null);
            reloadAll();
          }}
        />
      ) : fromTemplate ? (
        <TemplateCopyEditor
          client={client}
          source={fromTemplate}
          tags={(() => {
            const parsed = parseTagList(uploadTags);
            return 'error' in parsed ? [] : parsed.tags;
          })()}
          onClose={() => {
            setFromTemplate(null);
            reloadAll();
          }}
        />
      ) : null}

      {creatingTemplate ? (
        <TemplateEditor
          client={client}
          templateId={creatingTemplate}
          onClose={() => setCreatingTemplate(null)}
          onSaved={() => {
            setCreatingTemplate(null);
            reloadAll();
          }}
        />
      ) : null}

      {editingTemplate ? (
        <TemplateEditor
          client={client}
          asset={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSaved={() => {
            setEditingTemplate(null);
            reloadAll();
          }}
        />
      ) : null}

      {fitting ? (
        <HtmlFitModal
          client={client}
          assetIds={fitting}
          canWrite={canWrite}
          onClose={() => setFitting(null)}
          onChanged={reloadAll}
        />
      ) : null}

      {editingInfo ? (
        <AssetEditModal
          client={client}
          asset={editingInfo}
          onClose={() => setEditingInfo(null)}
          onSaved={() => {
            setEditingInfo(null);
            reloadAll();
          }}
        />
      ) : null}

      {validityOpen ? (
        <BulkValidityModal
          client={client}
          assetIds={[...selected]}
          onClose={() => setValidityOpen(false)}
          onSaved={() => {
            setValidityOpen(false);
            assets.reload();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * 업로드.
 *
 * 서버가 콘텐츠 주소로 중복을 제거하므로, 같은 파일을 다시 올려도 자산이 늘지 않는다.
 * 그 사실을 사용자에게 알려 준다 — 안 그러면 "올렸는데 안 늘었다"고 다시 올린다.
 */
/**
 * 콘텐츠 그리드.
 *
 * 사이니지 콘텐츠는 **눈으로 고르는 것**이다. "테스트 카드 1" 과 "테스트 카드 2" 는
 * 제목만으로는 구분되지 않지만 썸네일로는 한눈에 갈린다. 표는 정렬·비교가 필요할
 * 때를 위해 남겨 두고, 기본은 그리드로 둔다.
 *
 * 동작 버튼은 **올렸을 때만** 나타난다. 항상 떠 있으면 카드 수만큼 버튼이 늘어서
 * 정작 그림을 가린다. 다만 올릴 마우스가 없는 터치 화면에서는 늘 보인다 — 숨겨
 * 두면 보이지 않는 버튼을 손가락이 모르고 누른다.
 */
function AssetGrid({
  assets,
  selected,
  onToggleSelect,
  onOpen,
  onEdit,
  onArchive,
  onFit,
  canWrite,
}: {
  assets: Asset[];
  selected: Set<string>;
  onToggleSelect: (id: string) => void;
  onOpen: (asset: Asset) => void;
  onEdit: (asset: Asset) => void;
  onArchive: (asset: Asset) => void;
  /** 올린 HTML 을 적용할 화면에 맞춰 보는 창을 연다. */
  onFit: (asset: Asset) => void;
  /** 고칠 수 있는가(`content:write`). 없으면 카드 위의 단추를 내지 않는다. */
  canWrite: boolean;
}): ReactNode {
  /*
   * 하나라도 고르면 모든 카드의 체크박스를 상시 보이게 한다.
   *
   * 선택 중에는 다음 대상을 찾아 마우스를 옮기는데, 그때마다 체크박스가
   * 나타났다 사라지면 어디를 눌러야 하는지 매번 다시 찾게 된다.
   */
  const picking = selected.size > 0;

  return (
    <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {assets.map((asset) => (
        <div
          key={asset.id}
          className={`group relative overflow-hidden rounded-[10px] border bg-white transition-colors ${
            selected.has(asset.id) ? 'border-slate-800 ring-1 ring-slate-800' : 'border-slate-200 hover:border-slate-300'
          } ${asset.archived ? 'opacity-55' : ''}`}
        >
          <button
            type="button"
            className="block w-full text-left"
            onClick={() => onOpen(asset)}
            aria-label={`${asset.title} 상세`}
          >
            <AssetThumb asset={asset} />
            <div className="px-3 py-2.5">
              <p className="truncate text-[13.5px] font-medium text-slate-800" title={asset.title}>
                {asset.title}
              </p>
              <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-slate-400">
                <span>{TYPE_LABEL[asset.type]}</span>
                {asset.type !== 'web' && asset.type !== 'template' ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{formatBytes(asset.size)}</span>
                  </>
                ) : null}
              </p>
              {/*
                어느 편성에 들어 있는지.

                삭제가 막히는 이유가 여기 그대로 보인다. 눌러 보고 나서야 알게 되면,
                여러 개를 고른 경우 무엇 때문에 막혔는지 하나씩 되짚어야 한다.
              */}
              <p className="mt-1 truncate text-[11px] text-slate-400" title={usageTitle(asset)}>
                {usageLabel(asset)}
              </p>
            </div>
          </button>

          {/*
            선택 체크박스 — 좌상단.

            평소에는 숨어 있다가 마우스를 올리면 나타난다. 사이니지 콘텐츠는 눈으로
            고르는 것이라, 상시 표시하면 격자 전체에 네모가 깔려 그림을 가린다.
            터치 화면은 올릴 방법이 없으므로 늘 보인다.
          */}
          <label
            className={`absolute left-2 top-2 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md bg-white/95 ring-1 ring-slate-300 transition-opacity ${
              picking || selected.has(asset.id)
                ? 'opacity-100'
                : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100'
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={selected.has(asset.id)}
              onChange={() => onToggleSelect(asset.id)}
              aria-label={`${asset.title} 선택`}
            />
          </label>

          {/*
            상태는 항상 보인다 — 보관됨·처리 실패는 그림보다 먼저 알아야 한다.
            체크박스 자리를 항상 비워 둔다. 마우스를 올릴 때마다 배지가 밀리면
            읽던 글자가 움직인다.
          */}
          {asset.archived || assetStatusBadge(asset) || validityBadge(asset) ? (
            <div className="pointer-events-none absolute left-10 top-2">
              {asset.archived ? <Badge tone="neutral">보관됨</Badge> : null}
              <AssetStatusBadge asset={asset} />
              <ValidityBadgeView asset={asset} />
            </div>
          ) : null}

          {canWrite ? (
            <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100">
              <IconButton icon={Pencil} label="편집" subject={asset.title} onClick={() => onEdit(asset)} />
              {asset.type === 'html' ? (
                <IconButton icon={Scaling} label="화면 맞춤" subject={asset.title} onClick={() => onFit(asset)} />
              ) : null}
              <IconButton
                icon={asset.archived ? ArchiveRestore : Archive}
                label={asset.archived ? '복원' : '보관'}
                subject={asset.title}
                onClick={() => onArchive(asset)}
              />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * 사용처 한 줄.
 *
 * **쓰이지 않는다는 사실도 말한다.** 빈칸으로 두면 "아직 안 불러왔나" 와
 * "정말 안 쓰인다" 를 구분할 수 없고, 지워도 되는지 판단하려면 그 구분이 필요하다.
 */
export function usageLabel(asset: Pick<Asset, 'used_in'>): string {
  const used = asset.used_in;
  if (!used || used.length === 0) return '편성에 없음';
  if (used.length === 1) return `편성: ${usageName(used[0]!)}`;
  // 이름을 다 늘어놓으면 카드 너비를 넘겨 오히려 안 읽힌다. 나머지는 툴팁에서 본다.
  return `편성: ${usageName(used[0]!)} 외 ${used.length - 1}개`;
}

/** 전체 목록. 잘린 것을 확인할 수 있게 툴팁으로 붙인다. */
export function usageTitle(asset: Pick<Asset, 'used_in'>): string {
  const used = asset.used_in;
  if (!used || used.length === 0) return '어떤 편성에도 들어 있지 않습니다';
  return used.map(usageName).join(', ');
}

/**
 * 사용처 이름. 스케줄은 그렇다고 밝힌다 — 플레이리스트 목록에서 그 이름을 찾다가 못 찾는다.
 */
function usageName(entry: NonNullable<Asset['used_in']>[number]): string {
  return entry.kind === 'schedule' ? `스케줄 ‘${entry.name}’` : entry.name;
}

/**
 * 작은 썸네일 — 편성 편집처럼 한 줄에 들어가야 할 때.
 *
 * 그리드용 `AssetThumb` 과 나누는 이유는 크기가 아니라 **성격**이다. 그리드는
 * 고르는 화면이라 그림이 주인공이지만, 여기서는 순서·노출시간을 다루는 중에
 * "이게 뭐였더라" 를 확인하는 보조 수단이다. 줄 높이를 넘기면 목록을 훑기 어려워진다.
 */
/**
 * 담기 카드의 덧줄 — 썸네일만으로는 같은 그림의 다른 판(1080p·4K, 30초·15초)이 갈리지 않는다(2026-10-21).
 *
 * 파일이면 크기·해상도(·영상 길이), 템플릿이면 무슨 템플릿인지, 웹이면 사이트. 모르는 값은 빼고 잇는다.
 */
export function pickCardFacts(
  asset: Pick<Asset, 'type' | 'size' | 'width' | 'height' | 'duration_sec' | 'source_url' | 'template'>,
): string[] {
  if (asset.type === 'template') {
    const id = asset.template?.id as TemplateId | undefined;
    return id && TEMPLATE_META[id] ? [TEMPLATE_META[id].name] : [];
  }
  if (asset.type === 'web') {
    const site = siteOf(asset.source_url);
    return site ? [site.host] : [];
  }
  const facts: string[] = [];
  if (asset.size) facts.push(formatBytes(asset.size));
  if (asset.width && asset.height) facts.push(`${asset.width}×${asset.height}`);
  if (asset.type === 'video' && asset.duration_sec && asset.duration_sec > 0) facts.push(formatSeconds(asset.duration_sec));
  return facts;
}

/** 16:9 썸네일. 종류마다 보여줄 수 있는 것이 다르다 — 없는 것을 있는 척하지 않는다. */
function AssetThumb({ asset }: { asset: Asset }): ReactNode {
  if (asset.type === 'template' && asset.template) {
    return (
      <TemplateThumb
        template={asset.template}
        location="미리보기"
        className="aspect-video w-full overflow-hidden bg-slate-900"
      />
    );
  }

  if (asset.type === 'image' && asset.preview_url) {
    return (
      <div className="aspect-video w-full overflow-hidden bg-slate-100">
        <img src={asset.preview_url} alt="" className="h-full w-full object-cover" loading="lazy" />
      </div>
    );
  }

  if (asset.type === 'video') return <VideoThumb asset={asset} className="aspect-video w-full overflow-hidden bg-slate-100" />;
  if (asset.type === 'web') return <WebThumb asset={asset} className="aspect-video w-full overflow-hidden" live />;
  if (asset.type === 'html') {
    return <HtmlThumb asset={asset} className="aspect-video w-full overflow-hidden" />;
  }

  return (
    <div className="flex aspect-video w-full items-center justify-center bg-slate-100">
      <ImageIcon className="h-7 w-7 text-slate-300" strokeWidth={1.6} />
    </div>
  );
}

/**
 * 파일 업로드 상태.
 *
 * **메뉴 밖에 산다.** 예전에는 이 상태가 [콘텐츠 추가] 메뉴 안의 단추에 있었는데, 첫 파일이
 * 올라가 목록을 새로 고칠 때 메뉴가 닫히면서 단추와 함께 사라졌다 — "올리는 중… 3/7" 도,
 * 마지막의 실패·중복 안내도 아무도 보지 못했다. 큰 영상이 100MB 벽에 막혀도 조용했다.
 *
 * PDF 는 쪽마다 그림으로 바꿔 올리고(`pdfPages.ts`), 여러 쪽이면 쪽 순서대로 담은 플레이리스트를 만든다(2026-10-28).
 */
function useUploader(
  client: SignageOneClient,
  onUploaded: () => void,
  /** 다 올린 뒤 — 올린(또는 이미 있던) HTML 의 id. 화면 맞춤 창을 띄운다. */
  onHtmlUploaded: (ids: string[]) => void = () => undefined,
  /** 여러 쪽 PDF 의 플레이리스트를 만들 수 있는가(`schedule:write`). 없으면 쪽 그림만 올리고 그렇다고 말한다. */
  canMakePlaylist = false,
): {
  /** `detail` — PDF 를 바꾸거나 쪽을 올리는 중일 때 그 파일 안에서 어디까지 왔는지. */
  progress: { done: number; total: number; detail?: string } | null;
  message: { text: string; warn: boolean } | null;
  start: (files: File[], tags?: readonly string[], validUntil?: string | null) => Promise<void>;
  clearMessage: () => void;
} {
  const [progress, setProgress] = useState<{ done: number; total: number; detail?: string } | null>(null);
  const [message, setMessage] = useState<{ text: string; warn: boolean } | null>(null);
  // 바깥 주소 알림이 고객사의 바깥 연결 막기를 따른다(2026-11-03).
  const { htmlOffline } = useBrand();

  const uploadOne = async (
    file: File,
    tags: readonly string[],
    validUntil: string | null,
    title: string = file.name,
  ): Promise<{
    deduplicated: boolean;
    id: string | null;
    type: string | null;
    dwellSec: number | null;
    external: string[] | null;
  }> => {
    const form = new FormData();
    form.append('title', title);
    form.append('file', file);
    // 게시 종료 — 이미 있는 파일이어도 서버가 이 날짜를 적는다(적은 것을 버리지 않는다).
    if (validUntil) form.append('valid_until', validUntil);
    /*
     * 태그는 쉼표 한 줄로 싣는다 — multipart 는 JSON 스키마를 지나지 않고, 서버가 같은
     * 한도로 나눈다(`parseTagField`). 이미 있는 파일이면 서버가 그 콘텐츠에 **더한다.**
     */
    if (tags.length > 0) form.append('tags', tags.join(','));

    // 업로드는 multipart 라 클라이언트의 JSON 경로를 쓰지 않는다.
    const response = await fetch(client.mediaUrl('/api/v1/assets/upload'), {
      method: 'POST',
      body: form,
      headers: uploadHeaders(client),
      // 세션 쿠키로 로그인한 경우 이 경로도 쿠키를 실어야 한다.
      credentials: 'include',
    });
    // 프록시(Cloudflare 100MB 등)가 끊으면 본문이 JSON 이 아니다 — 그때도 사람이 읽을 말을 남긴다.
    const body = (await response.json().catch(() => ({}))) as {
      message?: string;
      deduplicated?: boolean;
      id?: string;
      type?: string;
      default_dwell_sec?: number;
      html_external?: string[] | null;
    };
    if (!response.ok) throw new Error(body.message ?? uploadFailureText(response.status));
    return {
      deduplicated: Boolean(body.deduplicated),
      id: body.id ?? null,
      type: body.type ?? null,
      dwellSec: body.default_dwell_sec ?? null,
      external: body.html_external ?? null,
    };
  };

  /**
   * PDF 한 개 — 쪽 그림으로 바꿔 차례로 올리고, 여러 쪽이면 PDF 이름의 플레이리스트에 쪽 순서대로 담는다.
   * 돌려주는 것은 결과 한 줄. 쪽 하나라도 못 올리면 거기서 멈추고 던진다(앞 쪽들은 콘텐츠로 남는다 — 다시 올리면 중복으로 잡힌다).
   */
  const uploadPdf = async (
    file: File,
    tags: readonly string[],
    validUntil: string | null,
    onDetail: (text: string) => void,
  ): Promise<string> => {
    const pages = await pdfToPages(file, (done, total) => onDetail(`PDF 쪽을 그림으로 바꾸는 중 ${done}/${total}`));
    const items: PlaylistItem[] = [];
    let duplicates = 0;
    for (const [index, page] of pages.entries()) {
      onDetail(`PDF 쪽 올리는 중 ${index + 1}/${pages.length}`);
      const result = await uploadOne(page.file, tags, validUntil, page.title).catch((error: unknown) => {
        throw new Error(`${index + 1}쪽 — ${error instanceof Error ? error.message : '실패'}`);
      });
      if (result.deduplicated) duplicates += 1;
      if (result.id) items.push(blankItem(result.id, result.dwellSec ?? undefined));
    }
    const head = `${file.name} — ${pages.length}쪽을 그림 콘텐츠로 올렸습니다`;
    if (pages.length === 1) return duplicates === 1 ? `${file.name} — 이미 등록된 쪽이라 기존 콘텐츠를 사용합니다` : head;
    // 같은 PDF 를 다시 올렸으면 플레이리스트도 이미 있다 — 같은 이름으로 하나 더 만들지 않는다.
    if (duplicates === pages.length) return `${file.name} — 이미 등록된 PDF 라 기존 콘텐츠를 사용합니다(플레이리스트는 새로 만들지 않았습니다)`;
    if (!canMakePlaylist) return `${head}. 플레이리스트를 만들 권한이 없어 쪽 순서대로 묶지는 않았습니다`;
    const name = pdfBaseName(file.name);
    await client.createPlaylist({ name, items }).catch((error: unknown) => {
      throw new Error(`쪽 그림은 올렸지만 플레이리스트를 만들지 못했습니다 — ${error instanceof Error ? error.message : '실패'}`);
    });
    return `${head} · 플레이리스트 「${name}」에 쪽 순서대로 담았습니다`;
  };

  /**
   * 여러 파일을 **하나씩 차례로** 올린다.
   *
   * 한꺼번에 보내면 빠를 것 같지만, 사이니지 콘텐츠는 영상이 섞여 수백 MB가 되고
   * 콘솔은 매장·사무실 회선에서 열린다. 동시에 밀어 넣으면 업로드가 서로 대역을
   * 빼앗아 전체가 느려지고, 중간에 하나가 끊기면 어디까지 올라갔는지도 모호해진다.
   * 차례로 올리면 "3/7" 이 정확한 사실이 된다.
   *
   * **하나가 실패해도 멈추지 않는다.** 열 개 중 하나가 형식 문제로 거부됐다고
   * 나머지 아홉을 다시 고르게 만들 이유가 없다.
   */
  const uploadAll = async (files: File[], tags: readonly string[] = [], validUntil: string | null = null): Promise<void> => {
    setMessage(null);
    setProgress({ done: 0, total: files.length });

    let duplicates = 0;
    const failed: string[] = [];
    const html: string[] = [];
    const pdfs: string[] = [];
    // 바깥 주소에서 불러오는 HTML(2026-11-03) — 올린 뒤 한 줄씩 알린다(자세한 곳은 상세에).
    const external: string[] = [];

    for (const [index, file] of files.entries()) {
      try {
        if (isPdfFile(file)) {
          const detail = (text: string): void => setProgress({ done: index, total: files.length, detail: `${file.name} · ${text}` });
          pdfs.push(await uploadPdf(file, tags, validUntil, detail));
        } else {
          const result = await uploadOne(file, tags, validUntil);
          if (result.deduplicated) duplicates += 1;
          if (result.type === 'html' && result.id && !html.includes(result.id)) html.push(result.id);
          const note = result.type === 'html' ? externalSummary(result.external, htmlOffline) : null;
          if (note) external.push(`${file.name} — ${note}`);
        }
      } catch (error) {
        failed.push(`${file.name} — ${isPdfFile(file) ? pdfFailureText(error) : error instanceof Error ? error.message : '실패'}`);
      }
      setProgress({ done: index + 1, total: files.length });
      // 한 건씩 목록에 반영한다. 큰 묶음에서 끝까지 아무것도 안 보이면 멈춘 것처럼 느껴진다.
      onUploaded();
    }

    setProgress(null);
    const text = summarize(files.length, duplicates, failed, [...pdfs, ...external]);
    setMessage(text ? { text, warn: failed.length > 0 || external.length > 0 } : null);
    if (html.length > 0) onHtmlUploaded(html);
  };

  return { progress, message, start: uploadAll, clearMessage: () => setMessage(null) };
}

/**
 * 업로드 실패 사유 — 서버가 말하지 못하고 중간에서 끊겼을 때.
 *
 * 413 은 거의 언제나 파일 크기다. 인터넷 주소(Cloudflare)로 접속하면 서버에 닿기 전에 100MB 에서
 * 끊기므로, 숫자만 보여주면 사용자는 무엇을 해야 할지 모른다.
 */
export function uploadFailureText(status: number): string {
  if (status === 413) return '파일이 너무 큽니다(인터넷 주소로 접속 시 최대 100MB). 용량을 줄여 다시 업로드하세요';
  return `업로드 실패 (${status})`;
}

function UploadButton({ busy, onFiles }: { busy: boolean; onFiles: (files: File[]) => void }): ReactNode {
  return (
    <>
      <label
        className={`flex w-full cursor-pointer items-center gap-2.5 px-4 py-2.5 text-[13.5px] text-slate-700 hover:bg-slate-50 ${
          busy ? 'cursor-not-allowed opacity-60' : ''
        }`}
      >
        <ImageIcon className="h-4 w-4 text-slate-400" strokeWidth={1.9} />
        {busy ? '업로드 중…' : '파일 업로드'}
        <input
          type="file"
          hidden
          multiple
          // 영상은 MOV·MKV·AVI 도 고를 수 있다 — 서버가 화면이 푸는 모양으로 변환한다(변환이 없는 서버는 까닭을 말하고 거절한다).
          // HTML 은 우리 시스템 밖에서 만든 `.html` 한 파일이다 — 화면은 샌드박스에 가둬 띄운다.
          // PDF 는 여기서 쪽마다 그림으로 바꿔 올린다(`pdfPages.ts`).
          accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime,video/x-matroska,video/x-msvideo,.mov,.mkv,.avi,.html,.htm,text/html,application/pdf,.pdf"
          disabled={busy}
          onChange={(event) => {
            const files = [...(event.target.files ?? [])];
            event.target.value = '';
            if (files.length > 0) onFiles(files);
          }}
        />
      </label>
    </>
  );
}

/**
 * 업로드 결과를 한 문단으로.
 *
 * 순수 함수라 시험할 수 있다. **모두 성공하면 아무 말도 하지 않는다** — 목록에
 * 나타난 것으로 충분하고, 성공을 매번 알리면 정작 실패했을 때의 문장이 묻힌다.
 * 다만 PDF(`pdfs` — 파일마다 결과 한 줄)는 말한다: 올린 파일 하나가 쪽 수만큼의 콘텐츠와 플레이리스트가 되어,
 * 말하지 않으면 무엇이 생겼는지 목록을 뒤져야 안다.
 */
export function summarize(total: number, duplicates: number, failed: string[], pdfs: readonly string[] = []): string | null {
  const parts: string[] = [];

  if (failed.length > 0) {
    parts.push(`${total - failed.length}건 등록 · ${failed.length}건 실패`);
    parts.push(...failed);
  } else if (duplicates > 0) {
    // 중복은 실패가 아니다. 같은 파일을 두 번 올려도 자산이 늘지 않는다는 사실만 알린다.
    parts.push(
      duplicates === total
        ? '이미 등록된 파일이라 기존 콘텐츠를 사용합니다.'
        : `${duplicates}건은 이미 등록된 파일이라 기존 콘텐츠를 사용합니다.`,
    );
  }
  parts.push(...pdfs);

  return parts.length > 0 ? parts.join('\n') : null;
}

/**
 * 업로드 헤더.
 *
 * 전송 계층이 감춘 토큰을 여기서만 예외적으로 꺼내 쓴다. multipart 는 클라이언트의
 * JSON 경로를 탈 수 없기 때문이다. 호스트 브리지 경로에서는 업로드가 지원되지 않으며,
 * 그 경우 콘솔은 파일 업로드 버튼을 감춘다(§integration).
 */
function uploadHeaders(client: SignageOneClient): Record<string, string> {
  const token = (globalThis as { __SIGNAGEONE_TOKEN__?: string }).__SIGNAGEONE_TOKEN__;
  return token && client.transportKind !== 'host-bridge' ? { authorization: `Bearer ${token}` } : {};
}

function WebAssetModal({
  client,
  initialTags = '',
  onClose,
  onCreated,
}: {
  client: SignageOneClient;
  /** [콘텐츠 추가] 에서 적어 둔 태그. 그 자리에서 적고 이 경로로 들어온 사람의 뜻이다. */
  initialTags?: string;
  onClose: () => void;
  onCreated: () => void;
}): ReactNode {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [tags, setTags] = useState(initialTags);
  const [invalid, setInvalid] = useState<string | null>(null);
  const action = useAction();

  return (
    <Modal title="웹 URL 추가" onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          const parsed = parseTagList(tags);
          if ('error' in parsed) {
            setInvalid(parsed.error);
            return;
          }
          void action.run(
            () => client.createWebAsset({ title, source_url: url, tags: parsed.tags }),
            onCreated,
          );
        }}
      >
        {invalid ? <ErrorNote message={invalid} onDismiss={() => setInvalid(null)} /> : null}
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

        <label className="field">
          <span>제목</span>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </label>
        <label className="field">
          <span>
            URL
            <HelpTip
              text="외부 대시보드·공지 페이지를 화면에 그대로 띄웁니다. 사내 페이지 노출을 막기 위해 내부망 주소는 등록할 수 없습니다."
              label="URL 도움말"
            />
          </span>
          <input
            className="input"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/notice"
            required
          />
        </label>
        <label className="field">
          <span>태그</span>
          <input
            className="input"
            value={tags}
            placeholder="로비, 가을 프로모션"
            onChange={(e) => {
              setTags(e.target.value);
              setInvalid(null);
            }}
          />
          <small className="hint">쉼표로 구분합니다.</small>
        </label>

        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.pending}>
            {action.pending ? '추가 중…' : '추가'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────── 플레이리스트 ───────────────────────────

/** 새 항목의 기본값. 노출시간은 자산 종류가 정한다(영상은 길다). */
function blankItem(assetId: string, dwellSec: number | undefined): PlaylistItem {
  return { asset_id: assetId, dwell_sec: dwellSec ?? 8, transition: 'fade', enabled: true };
}

export const PLAYLIST_SORT_KEYS = ['name', 'duration', 'updated'] as const;
export type PlaylistSortKey = (typeof PLAYLIST_SORT_KEYS)[number];

/**
 * 항목 상태 — 거르개의 값.
 *
 * 빠진 것(보관·삭제된 콘텐츠가 담겨 있다)이 먼저다. 화면에는 줄어든 채 나가는데 목록만 보면
 * 멀쩡해 보여, 콘텐츠를 정리한 뒤 손볼 플레이리스트를 찾는 데 쓴다.
 */
export type PlaylistHealth = 'missing' | 'idle' | 'ok';
export const PLAYLIST_HEALTH_LABELS: Readonly<Record<PlaylistHealth, string>> = {
  missing: '빠진 콘텐츠 있음',
  idle: '나갈 콘텐츠 없음',
  ok: '정상',
};

export function playlistHealth(playlist: Pick<Playlist, 'enabled_count' | 'playable_count'>): PlaylistHealth {
  if (playlist.enabled_count > playlist.playable_count) return 'missing';
  return playlist.playable_count === 0 ? 'idle' : 'ok';
}

export function playlistSortValue(
  playlist: Playlist,
  key: PlaylistSortKey,
  assetById: ReadonlyMap<string, Asset>,
): SortValue {
  if (key === 'name') return playlist.name || null;
  if (key === 'updated') return Date.parse(playlist.updated_at);
  // 길이가 없는(나갈 것이 없는) 플레이리스트는 "—" 다 — 짧은 순 맨 앞을 차지하지 않게 뒤로.
  return cycleTime(playlist.items, assetById).seconds || null;
}

export function PlaylistsScreen({ client, me }: Props): ReactNode {
  const playlists = useAsync(() => client.allPlaylists(), [client]);
  /*
   * **보관한 것까지 받는다.** 편성에 이미 담긴 자산을 보관하면(콘텐츠 화면의 [보관]),
   * 활성만 받는 목록에서는 그 줄을 찾지 못해 "(삭제된 콘텐츠)" 와 내부 id 로 그렸다 —
   * 지워지지도 않았는데. 담는 판에서 거르는 것은 `visibleAssets` 가 한다.
   */
  const assets = useAsync(() => client.allAssets({ archived: 'all' }), [client]);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [blocked, setBlocked] = useState<{ id: string; name: string; blocked: BlockedBy } | null>(null);
  const action = useAction();

  const items = playlists.data?.items ?? [];
  /** 한 바퀴 시간을 셀 때 영상 길이를 본다 — 영상은 노출 시간이 아니라 끝까지 재생한다. */
  const assetById = useMemo(
    () => new Map((assets.data?.items ?? []).map((asset) => [asset.id, asset])),
    [assets.data],
  );

  /* 정렬·거르기 — 기본은 서버가 주는 순서(최근 수정)와 같다. */
  const [sort, toggleSort] = useTableSort<PlaylistSortKey>('playlists', PLAYLIST_SORT_KEYS, {
    key: 'updated',
    dir: 'desc',
  });
  /* 검색·필터는 주소에 산다(`?q=·health=`) — 편집을 마치고 돌아와도, 새로 고쳐도 그대로. */
  const url = useUrlQuery();
  const search = url.get('q');
  const [searchText, setSearch] = useUrlText(url, 'q');
  const health = url.list('health');
  const filters: Filters<'health'> = { health };
  const rows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('ko');
    const matched = needle ? items.filter((playlist) => playlist.name.toLocaleLowerCase('ko').includes(needle)) : items;
    return sortRows(applyFilters(matched, filters, (playlist) => playlistHealth(playlist)), sort, (playlist, key) =>
      playlistSortValue(playlist, key, assetById),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 필터는 주소에서 매번 새로 읽어 내용으로 본다.
  }, [items, search, health.join(','), sort, assetById]);
  const filtering = search.trim() !== '' || (filters.health?.length ?? 0) > 0;
  const sorting = (dirLabels: Record<SortDir, string>, firstDir: SortDir = 'asc') => ({
    sort,
    onSort: toggleSort,
    dirLabels,
    firstDir,
  });
  const truncated = Boolean(playlists.data?.next_cursor);
  /* 쪽 나누기 — 검색·거르기·정렬을 바꾸면 1쪽으로(`Pager`). */
  const paging = usePaging(url, 'playlists', JSON.stringify([search, health, sort.key, sort.dir]));
  const pageSlice = paginate(rows, paging.page, paging.size);
  /* 일괄 작업 — 고른 것 가운데 조건에 맞는 줄만 센다(`SelectionBar`). 다른 쪽에서 고른 것도 센다. */
  const canWrite = can(me, 'schedule:write');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const chosen = rows.filter((playlist) => selected.has(playlist.id));
  /** 쓰는 곳이 있어 지우지 않은 것 — 알림은 사라지므로 이름을 남겨 둔다. */
  const [skipped, setSkipped] = useState<string[]>([]);

  const deleteChosen = async (): Promise<void> => {
    const targets = chosen;
    const ok = await confirmDialog({
      title: `플레이리스트 ${targets.length}개를 삭제할까요?`,
      body: `${targets
        .slice(0, 5)
        .map((playlist) => playlist.name)
        .join(', ')}${targets.length > 5 ? ` 외 ${targets.length - 5}개` : ''}\n\n되돌릴 수 없습니다. 화면·스케줄·캠페인이 쓰고 있는 것은 건너뜁니다.`,
      confirmLabel: '삭제',
      danger: true,
    });
    if (!ok) return;
    void action.run(
      () =>
        runEach(targets, async (playlist) => {
          try {
            await client.deletePlaylist(playlist.id);
            return true;
          } catch (cause) {
            // 쓰는 곳 때문에 막힌 것만 건너뛴다 — 그 밖의 실패는 멈추고 알린다.
            if (!blockedBy(cause)) throw cause;
            return false;
          }
        }),
      ({ done, results }) => {
        setSelected(new Set());
        setSkipped(done.filter((_, index) => !results[index]).map((playlist) => playlist.name));
        playlists.reload();
      },
      ({ results }) => {
        const deleted = results.filter(Boolean).length;
        return deleted > 0 ? `플레이리스트 ${deleted}개를 삭제했습니다` : null;
      },
    );
  };

  return (
    <div className="screen">
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      {playlists.error ? <ErrorNote message={playlists.error} /> : null}

      <Panel
        title={`플레이리스트 ${items.length}개`}
        className="screen-fill"
        actions={
          <>
            {items.length > 0 ? (
              <input
                className="input input-search w-full sm:w-auto"
                type="search"
                placeholder="이름 검색"
                aria-label="플레이리스트 이름 검색"
                value={searchText}
                onChange={(event) => setSearch(event.target.value)}
              />
            ) : null}
            {/* 플레이리스트를 만드는 것은 편성 변경이다(`schedule:write`). */}
            {can(me, 'schedule:write') ? (
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                새 플레이리스트
              </button>
            ) : null}
          </>
        }
      >
        {playlists.loading && items.length === 0 ? <Loading /> : null}

        {!playlists.loading && items.length === 0 ? (
          <Empty
            title="플레이리스트가 없습니다"
            hint="새 플레이리스트를 만들어 콘텐츠를 순서대로 묶으세요."
          />
        ) : null}

        {truncated ? (
          <div className="filter-note" role="status">
            <span>
              플레이리스트가 {LIST_CAP.toLocaleString('ko-KR')}개를 넘어 최근 수정한 {items.length.toLocaleString('ko-KR')}개만
              보입니다. 정렬·검색·필터도 그 안에서만 됩니다.
            </span>
          </div>
        ) : null}

        {filtering && items.length > 0 ? (
          <FilterNote
            shown={rows.length}
            total={items.length}
            unit="개"
            onClear={() => {
              url.set({ q: null, health: null });
            }}
          />
        ) : null}

        {chosen.length > 0 ? (
          <SelectionBar
            count={chosen.length}
            unit="개"
            onCancel={() => setSelected(new Set())}
            note={
              <PageSelectionNote
                pageIds={pageSlice.rows.map((playlist) => playlist.id)}
                allIds={rows.map((playlist) => playlist.id)}
                selected={selected}
                unit="개"
                onChange={setSelected}
              />
            }
          >
            <button type="button" className="btn btn-danger" disabled={action.pending} onClick={() => void deleteChosen()}>
              삭제
            </button>
          </SelectionBar>
        ) : null}

        {skipped.length > 0 ? (
          <div className="filter-note" role="status">
            <span>
              쓰는 곳이 있어 지우지 않았습니다: <strong>{skipped.join(', ')}</strong> — 하나씩 [삭제] 를 누르면 어디서 쓰는지
              보입니다.
            </span>
            <button type="button" className="link" onClick={() => setSkipped([])}>
              닫기
            </button>
          </div>
        ) : null}

        {items.length > 0 ? (
          <div className="table-scroll">
          <table className="table min-w-[32rem]">
            <thead>
              <tr>
                {canWrite ? (
                  <th className="col-check">
                    <SelectAll ids={pageSlice.rows.map((playlist) => playlist.id)} selected={selected} onChange={setSelected} />
                  </th>
                ) : null}
                <ColumnHead label="이름" column="name" sorting={sorting({ asc: '가나다순', desc: '가나다 역순' })} />
                <ColumnHead
                  label="항목"
                  column="health"
                  filter={
                    <FilterMenu
                      label="항목 상태"
                      options={filterOptions(
                        items,
                        (playlist) => playlistHealth(playlist),
                        (value) => ({ label: PLAYLIST_HEALTH_LABELS[value as PlaylistHealth] ?? value }),
                        filters.health ?? [],
                        fixedOrder(['missing', 'idle', 'ok']),
                      )}
                      chosen={filters.health ?? []}
                      onChange={(chosen) => url.set({ health: chosen })}
                    />
                  }
                />
                <ColumnHead label="총 길이" column="duration" sorting={sorting({ asc: '짧은 순', desc: '긴 순' })} />
                <ColumnHead
                  label="수정"
                  column="updated"
                  sorting={sorting({ asc: '오래된 순', desc: '최근 순' }, 'desc')}
                />
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="row-empty">
                  <td colSpan={canWrite ? 6 : 5}>조건에 맞는 플레이리스트가 없습니다.</td>
                </tr>
              ) : null}
              {pageSlice.rows.map((playlist) => (
                <tr key={playlist.id}>
                  {canWrite ? (
                    <td className="col-check">
                      <input
                        type="checkbox"
                        aria-label={`${playlist.name} 선택`}
                        checked={selected.has(playlist.id)}
                        onChange={() =>
                          setSelected((current) => {
                            const next = new Set(current);
                            if (next.has(playlist.id)) next.delete(playlist.id);
                            else next.add(playlist.id);
                            return next;
                          })
                        }
                      />
                    </td>
                  ) : null}
                  {/* 이름을 누르면 편집으로 간다 — 고칠 수 없는 사람에게는 글자로만(`ItemTitle`). */}
                  <td>
                    <ItemTitle
                      onOpen={can(me, 'schedule:write') ? () => setEditing(playlist.id) : undefined}
                      label={`${playlist.name} 편집`}
                    >
                      {playlist.name}
                    </ItemTitle>
                  </td>
                  {/*
                    **나가는 수를 센다.** 예전에는 줄의 스위치만 세어(`enabled_count`),
                    담긴 자산을 보관하면 "2 / 2" 인데 화면에는 하나만 나갔다. 목록을 믿고
                    편성을 짠 사람은 화면 앞에 서기 전까지 그것을 모른다.
                    빠진 것이 있으면 몇 개인지 함께 적는다 — 숫자만 줄면 왜 줄었는지 알 수 없다.
                  */}
                  <td className="cell-muted">
                    {playlist.playable_count} / {playlist.item_count}
                    {playlist.enabled_count > playlist.playable_count ? (
                      <span
                        className="ml-1 text-[11.5px] text-amber-700"
                        title="보관·삭제된 콘텐츠입니다. 편집에서 빼거나 복원하세요."
                      >
                        · {playlist.enabled_count - playlist.playable_count}개 빠짐
                      </span>
                    ) : null}
                  </td>
                  <td className="cell-muted" title={CYCLE_HINT}>
                    {formatCycle(cycleTime(playlist.items, assetById))}
                  </td>
                  <td className="cell-muted"><RelTime iso={playlist.updated_at} /></td>
                  <td className="col-actions">
                    {/* 플레이리스트를 고치는 것은 편성 변경이다(`schedule:write`). */}
                    {can(me, 'schedule:write') ? (
                      <>
                        <IconButton icon={Pencil} label="편집" subject={playlist.name} onClick={() => setEditing(playlist.id)} />
                        {/*
                          복제·삭제는 [⋯] 뒤로(점진적 공개). 복제는 비슷한 편성을 처음부터 다시 담지 않게 — 사본을 만들고
                          곧바로 그 편집 창을 연다.
                        */}
                        <MoreMenu
                          label={`${playlist.name} 작업`}
                          items={[
                            {
                              label: '복제',
                              disabled: action.pending,
                              onSelect: () =>
                                void action.run(
                                  () =>
                                    client.createPlaylist({
                                      name: copyName(playlist.name, items.map((other) => other.name)),
                                      description: playlist.description,
                                      items: playlist.items,
                                      default_transition: playlist.default_transition,
                                    }),
                                  (created) => {
                                    playlists.reload();
                                    setEditing(created.id);
                                  },
                                  (created) => `"${created.name}" 를 만들었습니다`,
                                ),
                            },
                            {
                              label: '삭제',
                              danger: true,
                              disabled: action.pending,
                              onSelect: async () => {
                                const ok = await confirmDialog({
                                  title: '플레이리스트를 삭제할까요?',
                                  body: <><strong>{playlist.name}</strong> — 되돌릴 수 없습니다.</>,
                                  confirmLabel: '삭제',
                                  danger: true,
                                });
                                if (!ok) return;
                                void action.run(
                                  async () => {
                                    try {
                                      await client.deletePlaylist(playlist.id);
                                      return true;
                                    } catch (cause) {
                                      // 쓰는 곳 때문에 막혔으면 어디서 쓰는지 목록으로 — 문장 한 줄로는 찾아갈 수 없다.
                                      const found = blockedBy(cause);
                                      if (!found) throw cause;
                                      setBlocked({ id: playlist.id, name: playlist.name, blocked: found });
                                      return false;
                                    }
                                  },
                                  playlists.reload,
                                  (deleted) => (deleted ? `"${playlist.name}" 플레이리스트를 삭제했습니다` : null),
                                );
                              },
                            },
                          ]}
                        />
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : null}

        <Pager slice={pageSlice} paging={paging} unit="개" />
      </Panel>

      {blocked ? (
        <BlockedDeleteModal
          subject="playlist"
          name={blocked.name}
          blocked={blocked.blocked}
          force={{
            candidates: items.filter((playlist) => playlist.id !== blocked.id).map(({ id, name }) => ({ id, name })),
            run: async (release) => {
              await client.deletePlaylist(blocked.id, release);
              playlists.reload();
            },
          }}
          onClose={() => setBlocked(null)}
        />
      ) : null}

      {creating ? (
        <CreatePlaylistModal
          client={client}
          onClose={() => setCreating(false)}
          /*
           * 만든 즉시 그 플레이리스트의 편집 창으로 넘긴다.
           *
           * 빈 플레이리스트는 아무 화면에도 쓸모가 없어, 만든 사람이 다음에 할 일은
           * 언제나 "콘텐츠를 담는" 하나뿐이다. 창을 닫고 목록에서 방금 만든 이름을
           * 다시 찾게 하는 것은 그 하나를 사용자에게 숙제로 넘기는 것이다.
           */
          onCreated={(created) => {
            setCreating(false);
            setEditing(created.id);
            playlists.reload();
          }}
        />
      ) : null}

      {editing ? (
        <PlaylistEditor
          client={client}
          playlistId={editing}
          assets={assets.data?.items ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            playlists.reload();
          }}
        />
      ) : null}
    </div>
  );
}

/** 노출 시간 칸 — 서버(`playlistItemSchema`)와 같은 범위. */
const DWELL_RULE: NumberRule = { label: '노출 시간', min: 1, max: 3600, unit: '초' };

/**
 * 편집 중인 편성 항목 — 노출 시간 칸을 비운 동안은 `null` 이다(지운 칸이 0 으로 되돌아가지 않게). 저장할 때 막는다.
 */
type DraftItem = Omit<PlaylistItem, 'dwell_sec'> & { dwell_sec: number | null };

/** 저장할 항목. 막히지 않고 여기 왔다면 비운 칸은 영상(칸이 없다 — 노출 시간을 쓰지 않는다)뿐이라 기본값을 채운다. */
function savedItem(item: DraftItem): PlaylistItem {
  return { ...item, dwell_sec: item.dwell_sec ?? 8 };
}

/** 노출 시간을 고쳐야 저장할 수 있는 항목인가 — 영상은 칸이 없다(끝까지 재생). */
function dwellProblem(item: DraftItem, asset: Pick<Asset, 'type'> | undefined): string | null {
  return asset?.type === 'video' ? null : numberProblem(item.dwell_sec, DWELL_RULE);
}

/**
 * 항목 하나가 화면에 머무는 시간(초). **영상은 끝까지 재생하므로 영상 길이다** — 노출 시간은 쓰지 않는다.
 *
 * 길이를 모르는 영상은 `null` 이다. 예전에는 노출 시간으로 어림했는데, 영상의 노출 시간은 끝을 기다리는 상한(기본
 * 300초)이라 30초짜리 영상 하나가 한 바퀴를 5분 넘게 늘렸다(2026-10-21). 서버가 뜰 때 옛 영상의 길이를 재 두므로
 * (`services/video-facts.ts`) 남는 것은 드물다.
 */
export function itemSeconds(
  item: { dwell_sec: number | null },
  asset: Pick<Asset, 'type' | 'duration_sec'> | undefined,
): number | null {
  if (asset?.type === 'video') return asset.duration_sec && asset.duration_sec > 0 ? asset.duration_sec : null;
  // 편집 중에 비운 칸은 아직 값이 없다 — 한 바퀴에 넣지 않는다(저장은 막힌다).
  return item.dwell_sec ?? 0;
}

/**
 * 화면이 이 항목을 트는가 — 해석 엔진이 빼는 것과 같은 조건(`packages/core/src/resolve.ts`·서버 `playable_count`):
 * 꺼 둔 줄, 지운 콘텐츠, 보관한 콘텐츠, 변환 중·실패한 영상, 지금 게시 기간 밖. 이것들을 한 바퀴에 넣으면 목록의
 * "재생되는 수" 와 총 길이가 서로 다른 편성을 말한다.
 */
export function itemPlays(
  item: { enabled: boolean },
  asset: Pick<Asset, 'archived' | 'status' | 'valid_from' | 'valid_until'> | undefined,
  at: Date,
): boolean {
  if (!item.enabled || !asset || asset.archived) return false;
  if (asset.status && asset.status !== 'ready') return false;
  const t = at.getTime();
  if (asset.valid_from && t < Date.parse(asset.valid_from)) return false;
  if (asset.valid_until && t >= Date.parse(asset.valid_until)) return false;
  return true;
}

/** 한 바퀴 — 트는 항목의 시간 합과, 길이를 몰라 세지 못한 영상 수(끝까지 트므로 그만큼 더 걸린다). */
export interface CycleTime {
  seconds: number;
  unknownVideos: number;
}

export function cycleTime(
  items: readonly DraftItem[],
  assetById: ReadonlyMap<string, Asset>,
  at: Date = new Date(),
): CycleTime {
  let seconds = 0;
  let unknownVideos = 0;
  for (const item of items) {
    const asset = assetById.get(item.asset_id);
    if (!itemPlays(item, asset, at)) continue;
    const length = itemSeconds(item, asset);
    if (length === null) unknownVideos += 1;
    else seconds += length;
  }
  return { seconds, unknownVideos };
}

/** "1분 3초" · 길이를 모르는 영상이 있으면 "1분 3초 + 영상 1개" — 세지 못한 것을 숫자에 숨기지 않는다. 트는 것이 없으면 "—". */
export function formatCycle(cycle: CycleTime): string {
  if (cycle.unknownVideos === 0) return cycle.seconds === 0 ? '—' : formatSeconds(cycle.seconds);
  const unknown = `영상 ${cycle.unknownVideos}개`;
  return cycle.seconds === 0 ? `${unknown}(길이 모름)` : `${formatSeconds(cycle.seconds)} + ${unknown}`;
}

/**
 * 일괄 적용(2026-10-21) — [전체 적용] 을 누를 때 정해 둔 노출 시간·전환을 모든 줄에 넣는다.
 *
 * 예전에는 전환 칸이 고르는 즉시 모든 줄을 바꾸고 [전체 적용] 은 노출 시간만 넣어, 두 칸을 정하고 단추를 누른
 * 사람은 전환이 이미 바뀌었는지, 단추가 무엇을 바꾸는지 알 수 없었다. 이제 두 칸은 정해 두는 칸이고 단추 하나가
 * 둘 다 넣는다.
 *
 * - 노출 시간은 **영상에 넣지 않는다** — 영상은 노출 시간과 상관없이 끝까지 재생한다.
 * - `null`·`''`(줄마다 달라 "혼합" 인 채 둔 칸)은 그대로 둔다 — 전환만 맞추려다 노출 시간까지 뭉개지 않게.
 * - 바뀌지 않는 줄은 같은 객체로 돌려준다 — 바뀌는 것이 있는지(`단추를 켤지`)를 그것으로 센다.
 */
export function applyToAll<T extends { asset_id: string; dwell_sec: number | null; transition: PlaylistItem['transition'] }>(
  items: readonly T[],
  change: { dwell: number | null; transition: PlaylistItem['transition'] | '' },
  isVideo: (assetId: string) => boolean,
): T[] {
  return items.map((row) => {
    const dwell = change.dwell !== null && !isVideo(row.asset_id) && row.dwell_sec !== change.dwell ? change.dwell : null;
    const transition = change.transition !== '' && row.transition !== change.transition ? change.transition : null;
    if (dwell === null && transition === null) return row;
    return { ...row, ...(dwell !== null ? { dwell_sec: dwell } : {}), ...(transition !== null ? { transition } : {}) };
  });
}

/** 영상이 아닌 줄의 노출 시간이 모두 같으면 그 값, 다르거나 그런 줄이 없으면 `null`("혼합"). */
export function commonDwell(
  items: readonly { asset_id: string; dwell_sec: number | null }[],
  isVideo: (assetId: string) => boolean,
): number | null {
  const values = new Set(items.filter((row) => !isVideo(row.asset_id)).map((row) => row.dwell_sec));
  return values.size === 1 ? [...values][0]! : null;
}

/** 총 길이에 마우스를 올리면 — 무엇을 셌는지. */
const CYCLE_HINT =
  '화면에 실제로 나가는 항목만 더합니다(꺼 둔 항목·보관·삭제·게시 기간 밖은 뺍니다). 영상은 끝까지 재생하므로 영상 길이로 셉니다. “+ 영상 N개” 는 길이를 아직 모르는 영상입니다.';

/**
 * 새 플레이리스트 — 이름만 받고 **곧바로 편집 창으로 넘긴다.**
 *
 * 만들고 → 목록에서 방금 만든 이름을 찾고 → 다시 누르는 세 걸음은, 다음에 할 일이
 * 하나뿐인데도 그것을 사용자가 스스로 찾아내게 했다. 여기서 이름부터 받는 이유는
 * 이름이 나중에 바꿀 수 없는 유일한 값이기 때문이다.
 */
function CreatePlaylistModal({
  client,
  onClose,
  onCreated,
}: {
  client: SignageOneClient;
  onClose: () => void;
  onCreated: (playlist: Playlist) => void;
}): ReactNode {
  const [name, setName] = useState('');
  const action = useAction();

  return (
    <Modal title="새 플레이리스트" onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          void action.run(() => client.createPlaylist({ name }), onCreated);
        }}
      >
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        <label className="field">
          <span>이름</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="1층 로비 기본"
            autoFocus
            required
          />
          <small className="hint">나중에 편집 창에서 바꿀 수 있습니다.</small>
        </label>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.pending}>
            {action.pending ? '만드는 중…' : '만들기'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * 플레이리스트 편집기 — **왼쪽이 결과, 오른쪽이 재료다.**
 *
 * 한때 [콘텐츠 담기] 가 담는 판을 열고 닫았다. 열 때마다 편성 목록이 아래로 밀려
 * 방금 보던 줄을 놓쳤고, "담는 곳" 과 "담긴 곳" 이 같은 자리를 번갈아 차지해 둘의
 * 관계가 보이지 않았다. 나란히 두면 고르는 순간 순서가 쌓이는 것이 그대로 보인다.
 *
 * 저장은 명시적이다 — 편성 변경은 즉시 전국 화면에 반영되므로, 실수로 저장되면 안 된다.
 */
function PlaylistEditor({
  client,
  playlistId,
  assets,
  onClose,
  onSaved,
}: {
  client: SignageOneClient;
  playlistId: string;
  assets: Asset[];
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const playlist = useAsync(() => client.getPlaylist(playlistId), [client, playlistId]);
  const [items, setItems] = useState<DraftItem[] | null>(null);
  /**
   * 고친 이름 — 손대기 전에는 null(서버 값을 따른다). 예전에는 만들 때 한 번 적으면 바꿀 길이 없었다(2026-10-20).
   * 이름은 목록·스케줄·캠페인·화면 편성에 보이는 이름표일 뿐, 다른 곳은 id 로 가리키므로 바꿔도 편성은 그대로다.
   */
  const [name, setName] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** 담기 판의 형식 거르기. 콘텐츠 목록과 같은 말을 쓴다. */
  const [pickerType, setPickerType] = useState<TypeFilter>('all');
  /**
   * 일괄 적용 칸에 적은 값 — `undefined` 면 손대지 않았다는 뜻이라 지금 목록의 공통값을 보인다(줄을 하나씩 고치면
   * 따라 바뀐다). 노출 시간을 비운 동안은 null — 지우는 중에 0 으로 튀지 않는다.
   */
  const [bulkDwellDraft, setBulkDwellDraft] = useState<number | null | undefined>(undefined);
  const [bulkTransitionDraft, setBulkTransitionDraft] = useState<PlaylistItem['transition'] | undefined>(undefined);
  /** [저장] 을 눌렀는데 노출 시간을 고쳐야 할 항목이 있었다 — 그 칸들을 짚는다(고치면 바로 풀린다). */
  const [showErrors, setShowErrors] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  /** 끌고 있는 항목과 놓을 자리. */
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  /**
   * 손잡이를 누르고 있는 동안에만 끌 수 있게 한다.
   *
   * 줄 전체를 항상 `draggable` 로 두면 **칸 안의 글자를 선택할 수 없다** —
   * 노출 시간을 고치려고 숫자를 드래그하면 줄이 끌려간다.
   */
  const [dragArmed, setDragArmed] = useState(false);
  const action = useAction();

  const current: DraftItem[] = items ?? playlist.data?.items ?? [];
  const currentName = name ?? playlist.data?.name ?? '';
  const renamed = name !== null && name.trim() !== playlist.data?.name;
  /* 고친 것이 있으면 닫기 전에 묻는다 — 처음 값은 편성을 다 읽은 뒤에 잡는다(`useDirty`). */
  const close = useGuardedClose(useDirty({ items: current, name: currentName }, Boolean(playlist.data)), onClose);
  /**
   * 편성을 다 읽기 전에는 담지 않는다. 그때 담으면 빈 목록에 붙어 원래 항목이 사라지고, 그대로 [저장] 하면
   * 편성이 고른 것 하나로 덮인다(2026-10-07 설명서 촬영에서 드러남 — 창을 열자마자 누르면 일어난다).
   */
  const loaded = items !== null || Boolean(playlist.data);
  const assetById = new Map(assets.map((asset) => [asset.id, asset]));
  /** 지금 편성에 들어 있는 콘텐츠. 체크 표시가 곧 이것이다. */
  /** 담긴 횟수 — 같은 콘텐츠를 앞뒤로 두 번 넣는 편성이 흔하다(카드에 "담김 2"). */
  const pickedCount = new Map<string, number>();
  for (const row of current) pickedCount.set(row.asset_id, (pickedCount.get(row.asset_id) ?? 0) + 1);

  const needle = search.trim().toLowerCase();
  /*
   * 담는 판에는 **보관한 것을 내지 않는다.** 보관은 "이제 쓰지 않는다" 는 뜻이라, 새로
   * 담을 후보로 다시 올라오면 보관한 의미가 없다. 이미 담겨 있는 줄에는 보관된 자산도
   * 그려야 하므로(`assetById`) 거르는 것은 이 판에서만 한다.
   */
  const visibleAssets = assets.filter(
    (asset) =>
      !asset.archived &&
      (pickerType === 'all' || asset.type === pickerType) &&
      (needle ? asset.title.toLowerCase().includes(needle) : true),
  );

  /**
   * 체크하면 담기고, 풀면 빠진다.
   *
   * **[담기] 단추를 없앴다.** 고르는 것과 담는 것을 두 단계로 두었더니, 스무 장을 담으려면
   * 고르고 → 담고 → 다시 열고를 되풀이해야 했고, 무엇보다 "체크했는데 편성에 없는" 중간
   * 상태가 생겼다 — 닫아 버리면 고른 것이 통째로 사라졌다. 체크가 곧 편성의 상태다.
   *
   * 영상도 다른 콘텐츠와 함께 담긴다 — 영상은 끝까지 재생한 뒤 다음으로 넘어간다(`content-rules.ts`).
   *
   * 푸는 것은 **그 콘텐츠를 전부** 뺀다. 같은 콘텐츠가 두 줄 들어 있을 때 한 줄만 빼면
   * 체크는 그대로 남아, 푼 사람이 무엇이 일어났는지 알 수 없다.
   */
  const toggleAsset = (asset: Asset): void => {
    if (!loaded) return;
    setItems(toggleContent(current, blankItem(asset.id, asset.default_dwell_sec)));
  };

  const reorder = (from: number, to: number): void => {
    setItems(reorderItems(current, from, to));
  };

  const move = (index: number, delta: number): void => reorder(index, index + delta);

  /**
   * 노출 시간·전환을 모든 줄에 한 번에(`applyToAll`).
   *
   * 스무 장을 담고 나면 대개 "다 5초로, 다 페이드로" 인데, 없으면 칸을 스무 번 고쳐야 한다. 두 칸에 정해 두고
   * [전체 적용] 을 누르면 둘 다 들어간다. 줄마다 다른 값은 "혼합" 으로 보이고, 혼합인 칸은 그대로 둔다 —
   * **모르는 사이에 하나로 뭉개지지 않게.**
   */
  const isVideo = (assetId: string): boolean => assetById.get(assetId)?.type === 'video';
  const transitions = new Set(current.map((row) => row.transition));
  const commonTransition = transitions.size === 1 ? [...transitions][0]! : '';
  const bulkDwell = bulkDwellDraft === undefined ? commonDwell(current, isVideo) : bulkDwellDraft;
  const bulkTransition = bulkTransitionDraft ?? commonTransition;
  const bulkDwellProblem = bulkDwell === null ? null : numberProblem(bulkDwell, DWELL_RULE);
  const bulkPreview = bulkDwellProblem ? current : applyToAll(current, { dwell: bulkDwell, transition: bulkTransition }, isVideo);
  const bulkChanges = bulkPreview.filter((row, index) => row !== current[index]).length;
  const applyBulk = (): void => {
    if (bulkDwellProblem || bulkChanges === 0) return;
    setItems(bulkPreview);
    // 칸은 다시 목록을 따른다 — 방금 넣은 값이 곧 공통값이다.
    setBulkDwellDraft(undefined);
    setBulkTransitionDraft(undefined);
    notify(`항목 ${bulkChanges}개에 적용했습니다 — 저장해야 화면에 반영됩니다`);
  };

  const update = (index: number, patch: Partial<DraftItem>): void => {
    setItems(current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const enabledCount = current.filter((row) => row.enabled).length;
  /** 노출 시간을 고쳐야 저장할 수 있는 줄 수 — [저장] 을 누른 뒤에만 짚는다. */
  const dwellIssues = current.filter((row) => dwellProblem(row, assetById.get(row.asset_id)) !== null).length;
  const nameError = currentName.trim() === '' ? '이름을 입력하세요' : null;
  /** 저장할 것이 있는가 — 항목을 고쳤거나 이름을 바꿨다. 이름을 비웠으면 눌러서 무엇이 잘못인지 듣게 켠다. */
  const canSave = items !== null || renamed || (name !== null && nameError !== null);

  const save = (): void => {
    if (dwellIssues > 0 || nameError) {
      setShowErrors(true);
      focusFirstInvalid(editorRef.current);
      return;
    }
    setShowErrors(false);
    const savedName = currentName.trim();
    void action.run(
      () =>
        client.updatePlaylist(playlistId, {
          // 고친 것만 보낸다 — 이름만 바꿨으면 편성(항목)은 건드리지 않는다.
          ...(items !== null ? { items: current.map(savedItem) } : {}),
          ...(renamed ? { name: savedName } : {}),
        }),
      onSaved,
      `"${savedName}" 를 저장했습니다`,
    );
  };
  /** 머리에 다는 한 줄 요약. 몇 개인지와 한 바퀴가 얼마인지는 편성의 두 가지 질문이다. */
  const summary =
    current.length === 0
      ? '비어 있음'
      : `${current.length}개${enabledCount < current.length ? ` · 사용 ${enabledCount}개` : ''} · 한 바퀴 ${formatCycle(cycleTime(current, assetById))}`;

  return (
    <Modal title="플레이리스트 편집" onClose={close} wide>
      {playlist.loading && !playlist.data ? <Loading /> : null}
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

      <div ref={editorRef}>
      <label className="field pl-name">
        <span>이름</span>
        <input
          className="input"
          value={currentName}
          maxLength={200}
          disabled={!playlist.data}
          aria-invalid={(showErrors && nameError !== null) || undefined}
          onChange={(event) => setName(event.target.value)}
        />
        {showErrors && nameError ? (
          <p className="field-error" role="alert">
            {nameError}
          </p>
        ) : null}
      </label>

      <div className="pl-editor">
        {/* ── 왼쪽: 재생 순서 ─────────────────────────────────── */}
        <section className="pl-pane">
          <header className="pl-pane-head">
            <h4 className="pl-pane-title">재생 순서</h4>
            <span className="pl-pane-note">{summary}</span>
          </header>

          {current.length === 0 ? (
            <Empty
              title="비어 있음"
              hint="오른쪽에서 콘텐츠를 선택하세요."
            />
          ) : (
            <>
              {/*
                노출 시간·전환 일괄 적용.

                스무 장을 담고 나면 대개 "다 5초로, 다 페이드로" 인데, 없으면 칸을 스무 번
                고쳐야 한다. 영상은 끝까지 재생하므로 노출 시간에서 빼 둔다.
                한 장뿐이면 "전체" 가 곧 그 줄이라 띄우지 않는다(점진적 공개) — 줄의 칸을 고치면 된다.
              */}
              {current.length < 2 ? null : (
              <div className="pl-bulk" role="group" aria-label="모든 항목에 한 번에 적용">
                {/* 정해 두는 칸 둘 — 왼쪽에서 오른쪽으로 읽고, 맨 끝의 [전체 적용] 하나가 둘 다 넣는다. */}
                <label className="pl-bulk-field">
                  <span>
                    노출 시간
                    <HelpTip
                      text="[전체 적용] 을 누르면 모든 항목의 노출 시간이 이 값이 됩니다. 영상은 끝까지 재생되므로 제외됩니다. 비워 두면(혼합) 노출 시간은 그대로 둡니다."
                      label="노출 시간 일괄 적용 도움말"
                    />
                  </span>
                  <NumberInput
                    rule={DWELL_RULE}
                    value={bulkDwell}
                    placeholder="혼합"
                    invalid={bulkDwellProblem !== null}
                    aria-label="일괄 적용할 노출 시간(초)"
                    onChange={setBulkDwellDraft}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        applyBulk();
                      }
                    }}
                  />
                  초
                </label>
                <span className="pl-bulk-field">
                  <label className="pl-bulk-field">
                    <span>전환</span>
                    <select
                      className="input input-narrow"
                      value={bulkTransition}
                      aria-label="일괄 적용할 전환 효과"
                      onChange={(event) => setBulkTransitionDraft(event.target.value as PlaylistItem['transition'])}
                    >
                      {bulkTransition === '' ? <option value="">혼합</option> : null}
                      {TRANSITION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <TransitionGuideButton current={commonTransition} onPick={setBulkTransitionDraft} />
                </span>
                <button
                  type="button"
                  className="btn btn-tiny btn-primary pl-bulk-apply"
                  disabled={bulkDwellProblem !== null || bulkChanges === 0}
                  title={
                    bulkDwellProblem
                      ? bulkDwellProblem
                      : bulkChanges === 0
                        ? '모든 항목이 이미 이 값입니다'
                        : `항목 ${bulkChanges}개를 이 노출 시간·전환으로 바꿉니다`
                  }
                  onClick={applyBulk}
                >
                  전체 적용
                </button>
              </div>
              )}
              {current.length >= 2 && bulkDwellProblem ? (
                <p className="field-error" role="alert">
                  {bulkDwellProblem}
                </p>
              ) : null}

              <ol className="pl-list">
                {current.map((item, index) => {
                  const asset = assetById.get(item.asset_id);
                  return (
                    <li
                      key={`${item.asset_id}-${index}`}
                      draggable={dragArmed}
                      onDragStart={() => setDragIndex(index)}
                      onDragOver={(event) => {
                        // 기본 동작을 막아야 놓기(drop)가 허용된다.
                        event.preventDefault();
                        setOverIndex(index);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        if (dragIndex !== null) reorder(dragIndex, index);
                        setDragIndex(null);
                        setOverIndex(null);
                        setDragArmed(false);
                      }}
                      onDragEnd={() => {
                        setDragIndex(null);
                        setOverIndex(null);
                        setDragArmed(false);
                      }}
                      className={`pl-row ${item.enabled ? '' : 'row-muted'} ${
                        dragIndex === index ? 'is-dragging' : ''
                      } ${overIndex === index && dragIndex !== null && dragIndex !== index ? 'is-over' : ''}`}
                    >
                      {/*
                        끌기 손잡이와 순번을 한 덩어리로 묶는다.

                        번호는 편성이 **순서로 뜻을 만드는 물건**이라는 것을 한눈에 말하고,
                        손잡이는 그 번호를 바꾸는 법을 바로 옆에서 말한다. 누르고 있는 동안에만
                        줄이 `draggable` 이 된다 — 항상 켜 두면 칸 안의 숫자를 드래그해 고칠 수
                        없다. 터치 화면에서는 손잡이를 감춘다(손가락은 `mousedown` 을 보내지
                        않아 끌기가 켜지지 않는다). 거기서도, 키보드에서도 ↑↓ 가 순서를 맡는다.
                      */}
                      <span
                        className="pl-grab"
                        title="끌어서 순서 변경"
                        onMouseDown={() => setDragArmed(true)}
                        onMouseUp={() => setDragArmed(false)}
                      >
                        <span className="pl-grab-dots" aria-hidden="true">
                          ⠿
                        </span>
                        <span className="pl-no">{index + 1}</span>
                      </span>

                      <AssetThumbSmall asset={asset} />

                      {/*
                        **보관과 삭제를 구분해 말한다.**

                        보관한 자산을 "(삭제된 콘텐츠)" 라고 부르면 두 번 틀린다 — 지워지지
                        않았는데 지워졌다고 하고, 되살릴 방법(보관함에서 되돌리기)이 있는데
                        없는 것처럼 들린다. 이 제품은 "쓰는 콘텐츠는 삭제되지 않는다" 를 규칙으로
                        삼고 보관을 그 대안으로 내미는데, 그 둘을 한 낱말로 뭉개면 규칙이 무너진다.
                      */}
                      <span className="pl-title">
                        <span className="block truncate">{asset?.title ?? '(삭제된 콘텐츠)'}</span>
                        <span className="pl-meta">
                          {asset
                            ? `${TYPE_LABEL[asset.type]}${
                                asset.type === 'video' && asset.duration_sec ? ` · ${formatSeconds(asset.duration_sec)}` : ''
                              }${asset.archived ? ' · 보관됨(재생 안 됨)' : ''}`
                            : '목록에서 제거하세요'}
                        </span>
                      </span>

                      {/*
                        노출 시간·전환·사용은 **한 덩어리**다. 줄마다 다르게 둘 수 있는 값이
                        여기 모여 있고, 순서를 바꾸는 단추(맨 오른쪽)와 섞이지 않는다.
                      */}
                      <span className="pl-knobs">
                        {/*
                          영상에는 노출 시간 칸이 없다 — 끝까지 재생한 뒤 다음으로 넘어간다. 칸을 남겨 두면
                          "8초로 정했는데 왜 2분을 트나" 하는 오해가 생긴다. 칸과 같은 폭을 차지해 줄이 맞는다.
                        */}
                        {asset?.type === 'video' ? (
                          <span className="pl-dwell pl-dwell-video" title="영상은 끝까지 재생한 뒤 다음 콘텐츠로 넘어갑니다">
                            끝까지 재생
                          </span>
                        ) : (
                          <label className="pl-dwell">
                            <NumberInput
                              rule={DWELL_RULE}
                              value={item.dwell_sec}
                              invalid={showErrors && dwellProblem(item, asset) !== null}
                              aria-label="노출 시간(초)"
                              onChange={(value) => update(index, { dwell_sec: value })}
                            />
                            초
                          </label>
                        )}
                        <select
                          className="input input-narrow"
                          value={item.transition}
                          title={TRANSITION_DESCRIPTIONS[item.transition]}
                          aria-label="전환 효과"
                          onChange={(e) =>
                            update(index, { transition: e.target.value as PlaylistItem['transition'] })
                          }
                        >
                          {TRANSITION_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <label className="checkbox" title="끄면 목록에 남긴 채 재생에서만 뺍니다">
                          <input
                            type="checkbox"
                            checked={item.enabled}
                            onChange={(e) => update(index, { enabled: e.target.checked })}
                          />
                          사용
                        </label>
                      </span>

                      <span className="pl-ops">
                        <button
                          type="button"
                          className="link"
                          onClick={() => move(index, -1)}
                          disabled={index === 0}
                          aria-label="위로"
                          title="위로"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="link"
                          onClick={() => move(index, 1)}
                          disabled={index === current.length - 1}
                          aria-label="아래로"
                          title="아래로"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="link link-danger"
                          onClick={() => setItems(current.filter((_, i) => i !== index))}
                          aria-label="제거"
                          title="목록에서 제거(콘텐츠는 유지)"
                        >
                          ✕
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ol>

              <p className="pl-foot">
                <span className="[@media(hover:none)]:hidden">⠿ 끌기 또는 ↑↓로 순서 변경</span>
                <span className="hidden [@media(hover:none)]:inline">↑↓로 순서 변경</span>
              </p>
            </>
          )}
        </section>

        {/* ── 오른쪽: 담을 콘텐츠 ─────────────────────────────── */}
        <section className="pl-pane">
          <header className="pl-pane-head">
            <h4 className="pl-pane-title">콘텐츠</h4>
            <span className="pl-pane-note">선택하면 왼쪽에 추가</span>
          </header>

          <div className="pl-pick-bar">
            <input
              className="input input-search flex-1"
              type="search"
              placeholder="제목 검색"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {/*
              형식 거르기. 콘텐츠가 쌓인 계정에서는 제목을 기억하지 못해도 "영상 중에서"
              라는 것은 안다 — 목록 화면과 같은 말(전체 형식·이미지·영상…)을 쓴다.
            */}
            <select
              className="input"
              value={pickerType}
              onChange={(event) => setPickerType(event.target.value as TypeFilter)}
              aria-label="형식 거르기"
            >
              <option value="all">전체 형식</option>
              {TYPE_ORDER.map((type) => (
                <option key={type} value={type}>
                  {TYPE_LABEL[type]}
                </option>
              ))}
            </select>
          </div>

          {visibleAssets.length === 0 ? (
            <p className="hint p-4">
              {assets.length === 0 ? '등록된 콘텐츠가 없습니다.' : '검색 결과가 없습니다.'}
            </p>
          ) : (
            /*
              썸네일 카드로 고른다. 사이니지 콘텐츠는 눈으로 고르는 것이라, 제목만 읽고 고르면 편성을 만든 뒤
              미리보기에서 틀린 것을 발견하게 된다. 그렇다고 그림만 있으면 비슷한 그림·같은 영상의 다른 판이 갈리지
              않는다 — 카드에 형식·제목(두 줄)·크기·해상도·길이를 함께 적는다(2026-10-21, `pickCardFacts`).

              이미 담긴 것도 고를 수 있게 둔다 — 같은 콘텐츠를 앞뒤로 두 번 넣는
              편성은 흔하다. 대신 담겼다는 사실(과 횟수)은 표시한다.
            */
            <div className="pl-pick-grid">
              {visibleAssets.map((asset) => {
                const count = pickedCount.get(asset.id) ?? 0;
                const already = count > 0;
                const facts = pickCardFacts(asset);
                return (
                  <label
                    key={asset.id}
                    className={`pl-card ${already ? 'is-picked' : ''}`}
                    title={already ? `${asset.title} · 선택 해제 시 목록에서 제거` : asset.title}
                  >
                    <input
                      type="checkbox"
                      className="pl-card-check"
                      checked={already}
                      disabled={!loaded}
                      onChange={() => toggleAsset(asset)}
                      aria-label={`${asset.title} ${already ? '빼기' : '담기'}`}
                    />
                    <AssetThumbSmall asset={asset} full />
                    <span className="pl-card-body">
                      <span className="pl-card-tags">
                        <span className="pl-card-type">{TYPE_LABEL[asset.type]}</span>
                        {already ? <span className="pl-card-picked">담김{count > 1 ? ` ${count}` : ''}</span> : null}
                        <ValidityBadgeView asset={asset} />
                      </span>
                      <span className="pl-card-title">{asset.title}</span>
                      <span className="pl-card-meta">{facts.length > 0 ? facts.join(' · ') : '\u00a0'}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}

        </section>
      </div>
      </div>

      {showErrors && dwellIssues > 0 ? (
        <p className="field-error" role="alert">
          노출 시간이 비었거나 범위를 벗어난 항목이 {dwellIssues}개 있습니다 — 빨간 칸에 {DWELL_RULE.min}~{DWELL_RULE.max}초를
          입력하세요.
        </p>
      ) : null}

      <div className="form-actions">
        {/* 왼쪽 끝 — 잘못 고쳤을 때 앞 판으로(되돌리면 이 창은 닫히고 목록이 새로 그려진다). */}
        <span className="mr-auto">
          <HistoryButton client={client} type="playlist" id={playlistId} canRestore onRestored={onSaved} />
        </span>
        <button type="button" className="btn" onClick={close}>
          취소
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={action.pending || !canSave}
          onClick={save}
        >
          {action.pending ? '저장 중…' : '저장'}
        </button>
      </div>
    </Modal>
  );
}

/** 게시 상태 배지 — 곧 끝나거나 끝났거나 시작 전일 때만. */
function ValidityBadgeView({ asset }: { asset: Asset }): ReactNode {
  const badge = validityBadge(asset);
  if (!badge) return null;
  return (
    <span title={badge.title}>
      <Badge tone={badge.tone}>{badge.label}</Badge>
    </span>
  );
}

/**
 * 고른 콘텐츠의 게시 기간을 한꺼번에 — 캠페인 소재 묶음을 같은 계약 기간으로.
 *
 * 보낸 두 값으로 **통째로 바꾼다.** 한 칸을 비우면 그쪽 끝 제한을 지운다는 뜻이라, 따로 정해 둔
 * 콘텐츠가 섞여 있으면 그 값이 덮인다는 것을 미리 적어 둔다.
 */
function BulkValidityModal({
  client,
  assetIds,
  onClose,
  onSaved,
}: {
  client: SignageOneClient;
  assetIds: string[];
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const [from, setFrom] = useState('');
  const [until, setUntil] = useState('');
  const [invalid, setInvalid] = useState<string | null>(null);
  const action = useAction();

  return (
    <Modal title={`게시 기간 — ${assetIds.length}건`} onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          const validity = readValidity(from, until);
          if ('error' in validity) {
            setInvalid(validity.error);
            return;
          }
          void action.run(
            () => client.setAssetValidity({ asset_ids: assetIds, ...validity }),
            onSaved,
            `콘텐츠 ${assetIds.length}건의 게시 기간을 정했습니다`,
          );
        }}
      >
        {invalid ? <ErrorNote message={invalid} onDismiss={() => setInvalid(null)} /> : null}
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        <ValidityFields
          from={from}
          until={until}
          onChange={(change) => {
            if (change.valid_from !== undefined) setFrom(change.valid_from);
            if (change.valid_until !== undefined) setUntil(change.valid_until);
            setInvalid(null);
          }}
        />
        <p className="hint">고른 콘텐츠의 게시 기간을 이 값으로 바꿉니다. 비운 칸은 그쪽 끝 제한을 지웁니다.</p>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.pending}>
            {action.pending ? '저장 중…' : '저장'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** 처리 상태 배지 — 변환 대기·진행률·실패(`transcode.ts`). 문제 없으면 그리지 않는다. */
function AssetStatusBadge({ asset }: { asset: Asset }): ReactNode {
  const badge = assetStatusBadge(asset);
  if (!badge) return null;
  return (
    <span title={badge.title}>
      <Badge tone={badge.tone}>{badge.label}</Badge>
    </span>
  );
}
